// IndexStrategiesPage.jsx – React conversion of IndexStrategiesPage.html
// Design Invelps · IBM Plex Mono · Cormorant Garamond · palette earthy
import { useState, useMemo, useEffect, useCallback, createContext, useContext, useRef } from "react";
import TickerSymbolLink from "../../../../components/TickerSymbolLink";
import {
  saveDrillReturnState,
  loadDrillReturnState,
  clearDrillReturnState,
} from "../../../../utils/drillModalStateStorage";
import ModalPortal from "../../../../components/ModalPortal";
import InvelpsTipMark from "../../../../components/InvelpsTooltip";
import { parseComp } from "./strategiesPageApiMapper";
import {
  resolveStockRegion,
  REGION_COLORS as REGION_COLOR_MAP,
} from "../../../../utils/invelpsRegions";

/* ─── TOKENS ────────────────────────────────────────────────── */
const C = { green:"#6f8a5b", amber:"#caa85e", orange:"#d28a52", red:"#c6523f", gold:"#b59a5b" };
const MONTHS = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];
const SECTOR_C = {
  "Information Technology":C.green,"Consumer Discretionary":C.amber,"Industrials":C.orange,
  "Financials":"#5b7a8a","Health Care":"#8da57a","Consumer Staples":"#a9b38a",
  "Energy":C.red,"Materials":"#6b8a6b","Real Estate":"#8a6b5b",
  "Communication Services":"#5b6b8a","Utilities":"#7a6b8a","Other":"#8a8a7a",
};
const CAP_C = { "Mega Cap":C.green,"Large Cap":C.amber,"Mid Cap":C.orange,"Small Cap":C.red,"Nano Cap":"#6b8a8a" };
function getRegion(c) {
  const { regionFr } = resolveStockRegion(c);
  return regionFr && regionFr !== "—" ? regionFr : "—";
}
const STYLE_T = {
  "Value":        { bg:"rgba(111,138,91,.15)", bd:"rgba(111,138,91,.4)",  col:C.green },
  "Growth":       { bg:"rgba(202,168,94,.15)", bd:"rgba(202,168,94,.4)",  col:C.amber },
  "GARP":         { bg:"rgba(210,138,82,.15)", bd:"rgba(210,138,82,.4)",  col:C.orange },
  "Quantitative": { bg:"rgba(91,122,138,.15)", bd:"rgba(91,122,138,.4)",  col:"#5b7a8a" },
  "Macro":        { bg:"rgba(169,179,138,.15)",bd:"rgba(169,179,138,.4)", col:"#6b7a4b" },
  "Contrarian":   { bg:"rgba(198,82,63,.13)",  bd:"rgba(198,82,63,.35)",  col:C.red },
  "Quality":      { bg:"rgba(141,165,122,.15)",bd:"rgba(141,165,122,.4)", col:"#6b8a5b" },
  "Income":       { bg:"rgba(181,154,91,.12)", bd:"rgba(181,154,91,.28)", col:C.gold },
  "_":            { bg:"rgba(181,154,91,.12)", bd:"rgba(181,154,91,.28)", col:C.gold },
};
const stT = s => STYLE_T[s] || STYLE_T._;
const AV = [C.green,C.amber,C.red,C.orange,"#5b7a8a","#8da57a","#6b8a6b","#8a6b5b",C.gold];
const avC = n => { let h=0; for(const c of n) h=(h*31+c.charCodeAt(0))%AV.length; return AV[h]; };
const hA = (hex,a) => { const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16); return `rgba(${r},${g},${b},${a})`; };
const fP = (v,d=1) => v==null?"—":`${+v>=0?"+":""}${(+v).toFixed(d)}%`;
const pC = v => v!=null&&+v>=0?C.green:C.red;
const sC = v => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "var(--muted)";
  return n >= 75 ? C.green : n >= 55 ? C.amber : C.red;
};
const fmtScore = (v, d = 1) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return d === 0 ? String(Math.round(n)) : n.toFixed(d);
};
const shC = v => v==null?"#94a3b8":v>1?C.green:v>0?C.amber:C.red;
const mosC = v => v>=20?C.green:v>=0?C.amber:C.red;
const MONO = { fontFamily:"'IBM Plex Mono',monospace" };
const SERIF = { fontFamily:"'Cormorant Garamond',Georgia,serif" };

/* ─── GLOBAL CSS ─────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=IBM+Plex+Mono:wght@400;600;700&display=swap');
:root{--bg:#fbfaf7;--paper:#fff;--paper-alt:#f5f3ee;--ink:#1f2a24;--muted:#6f7b74;
  --line:rgba(30,40,34,.09);--line2:rgba(30,40,34,.05);--row-h:rgba(181,154,91,.08);}
*{box-sizing:border-box;margin:0;padding:0}
html,body{width:100%;margin:0;padding:0;overflow-x:hidden;}
body{background:radial-gradient(1100px 320px at 50% 0%,rgba(181,154,91,.10),transparent 55%),linear-gradient(var(--bg),var(--bg));font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:var(--ink);}
#root,#app,[data-reactroot]{width:100%;}
/* kpi grid */
.isp-page-full{width:100%;min-height:100vh;background:var(--bg);box-sizing:border-box;}
.isp-strategies-main{
  max-width:min(1920px,100%);margin:0 auto;padding:26px 32px 40px;
  width:100%;box-sizing:border-box;
}
@media(max-width:1200px){.isp-strategies-main{padding:22px 18px 32px;}}
@media(max-width:768px){.isp-strategies-main{padding:16px 12px 24px;}}
.isp-kpi-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
@media(min-width:480px){.isp-kpi-grid{grid-template-columns:repeat(4,1fr)}}
/* sparkbar */
.isp-spark{display:flex;gap:1.5px;align-items:flex-end;height:20px;margin-top:8px}
.isp-spark span{flex:1;border-radius:1px;opacity:.85}
/* month heatmap */
.isp-mth-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:4px}
/* mini-kpi grids */
.mk4{display:grid;grid-template-columns:repeat(4,1fr);gap:8px}
.mk3{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}
.mk5{display:grid;grid-template-columns:repeat(5,1fr);gap:6px}
/* desktop strategy table — pleine largeur, texte lisible */
.isp-tbl-wrap{width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch;}
.isp-tbl{width:100%;min-width:100%;border-collapse:collapse;table-layout:fixed;}
.isp-tbl thead th{padding:14px 16px;font-size:12px;font-family:'IBM Plex Mono',monospace;
  letter-spacing:.10em;text-transform:uppercase;color:var(--muted);
  border-bottom:2px solid var(--line);white-space:nowrap;cursor:pointer;user-select:none;text-align:right}
.isp-tbl thead th.tl{text-align:left}
.isp-tbl thead th.sorted{color:${C.gold}}
.isp-tbl thead th .isp-th-sort{display:inline-flex;align-items:center;gap:6px;justify-content:flex-end;width:100%;}
.isp-tbl thead th.tl .isp-th-sort{justify-content:flex-start;}
.inv-sort-icon{display:inline-flex;flex-direction:column;align-items:center;justify-content:center;width:12px;height:14px;flex-shrink:0;gap:2px;line-height:0;vertical-align:middle;}
.inv-sort-icon i{display:block;width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;font-style:normal;padding:0;margin:0;}
.inv-sort-icon .inv-sort-up{border-bottom:5px solid rgba(30,40,34,.24);}
.inv-sort-icon .inv-sort-down{border-top:5px solid rgba(30,40,34,.24);}
.inv-sort-icon.idle .inv-sort-up,.inv-sort-icon.idle .inv-sort-down{border-bottom-color:rgba(30,40,34,.30);border-top-color:rgba(30,40,34,.30);}
.inv-sort-icon.asc .inv-sort-up{border-bottom-color:${C.gold};}
.inv-sort-icon.asc .inv-sort-down{border-top-color:rgba(30,40,34,.14);}
.inv-sort-icon.desc .inv-sort-up{border-bottom-color:rgba(30,40,34,.14);}
.inv-sort-icon.desc .inv-sort-down{border-top-color:${C.gold};}
.isp-tbl tbody tr{cursor:pointer;}
.isp-tbl tbody td{transition:background .12s;}
.isp-tbl tbody tr:hover td{background:var(--row-h);}
.isp-tbl tbody tr.sel td{background:var(--row-h);}
.isp-tbl tbody tr.sel td:first-child{box-shadow:inset 3px 0 0 ${C.gold};}
.isp-tbl tbody td{padding:14px 16px;font-size:15px;border-bottom:1px solid var(--line2);
  white-space:nowrap;vertical-align:middle;text-align:right}
.isp-tbl tbody td.tl{text-align:left}
.isp-tbl .isp-strat-name{font-size:15px;font-weight:800;color:var(--ink);}
.isp-tbl .isp-strat-sub{font-size:12px;color:var(--muted);margin-top:3px;}
.isp-tbl .isp-strat-val{font-family:'IBM Plex Mono',monospace;font-size:15px;font-weight:700;}
.isp-fbar input,.isp-fbar select,.isp-fsel{font-size:13px!important;}
.isp-kpi-grid .isp-kpi-val{font-size:20px!important;}
/* ── MODAL OVERLAY ── */
.isp-moverlay{
  position:fixed;inset:0;z-index:5000;
  background:rgba(10,18,14,.52);
  backdrop-filter:blur(5px);
  display:flex;align-items:center;justify-content:center;
  padding:max(20px,4vh) 20px;
  box-sizing:border-box;
  overflow-y:auto;
}
.isp-moverlay.mobile{padding:0;align-items:stretch;overflow:hidden;}
/* modal box — blanc pur, coins arrondis */
.isp-mbox{
  width:min(1520px,94vw);
  max-height:min(88vh,900px);
  margin:auto;
  border-radius:16px;
  background:#fff;
  border:1px solid rgba(30,50,38,.10);
  box-shadow:0 24px 64px rgba(10,18,14,.26);
  overflow:hidden;
  display:flex;flex-direction:column;
  flex-shrink:0;
}
.isp-mbox.mobile{width:100vw;height:100vh;max-height:100vh;border-radius:0;}
/* modal header — blanc, titre sombre */
.isp-mhd{
  background:#fff;
  border-bottom:1px solid rgba(30,50,38,.08);
  padding:18px 24px 14px;
  flex-shrink:0;
  position:relative;
}
.isp-mclose{
  position:absolute;top:16px;right:18px;z-index:2;
  width:38px;height:38px;padding:0;
  display:flex;align-items:center;justify-content:center;
  background:rgba(198,82,63,.12);
  border:1px solid rgba(198,82,63,.30);
  border-radius:10px;
  color:#c6523f;font-size:20px;font-weight:700;line-height:1;
  cursor:pointer;font-family:inherit;
  transition:background .15s,border-color .15s,transform .12s;
}
.isp-mclose:hover{background:rgba(198,82,63,.20);border-color:rgba(198,82,63,.48);transform:scale(1.04);}
/* modal tabs / action bar */
.isp-mtabs{
  display:flex;align-items:center;gap:0;padding:0 24px;
  border-bottom:1px solid rgba(30,50,38,.08);
  background:#fff;flex-shrink:0;min-height:44px;
}
.isp-mtab{
  padding:9px 16px;border-radius:0;font-size:11px;font-weight:700;
  cursor:pointer;border:none;border-bottom:2px solid transparent;
  background:transparent;color:#7a8a82;
  font-family:'IBM Plex Mono',monospace;transition:all .15s;
}
.isp-mtab:hover{color:#18221e;}
.isp-mtab.on{color:#18221e;border-bottom-color:${C.gold};font-weight:800;}
/* modal scrollable body — min-height:0 requis pour scroll flex sur mobile */
.isp-mbody{
  flex:1;min-height:0;overflow-y:auto;overflow-x:hidden;
  -webkit-overflow-scrolling:touch;overscroll-behavior:contain;
  padding:20px 24px 24px;background:#fff;
}
.isp-mfooter{
  flex-shrink:0;padding:12px 20px max(16px, env(safe-area-inset-bottom));
  text-align:center;font-size:11px;color:var(--muted);
  border-top:1px solid rgba(30,50,38,.08);background:#fff;
}
/* pbar */
.ct-pbar{width:100%;height:3px;border-radius:999px;background:rgba(30,50,38,.07);overflow:hidden;margin-top:3px}
.ct-pbar-fill{height:100%;border-radius:999px}
/* inv-chip */
.inv-chip{display:inline-flex;align-items:center;gap:5px;background:#f7f5f0;
  border:1px solid rgba(30,50,38,.09);border-radius:999px;padding:3px 8px 3px 4px;
  cursor:pointer;transition:all .15s}
.inv-chip:hover{border-color:${C.gold};background:#faf7ef;}
html[data-theme="dark"] .inv-chip{
  background:transparent !important;
  border:1px solid rgba(111,138,91,.45) !important;
}
html[data-theme="dark"] .inv-chip:hover{
  background:rgba(111,138,91,.10) !important;
  border-color:rgba(111,138,91,.65) !important;
}
html[data-theme="dark"] .inv-chip .inv-chip-name{
  color:#8fb878 !important;
}
/* ── FILTER BAR ── */
.isp-fbar{
  display:flex;flex-wrap:wrap;gap:6px;align-items:center;
  padding:10px 14px;
  background:#f7f5f0;
  border:1px solid rgba(30,50,38,.08);
  border-radius:10px;
  margin-bottom:12px;
}
.isp-fbar-label{
  font-size:9px;font-weight:800;text-transform:uppercase;
  letter-spacing:.11em;color:#7a8a82;white-space:nowrap;margin-right:2px;
}
/* filter select — underline style like production */
.isp-fsel{
  appearance:none;-webkit-appearance:none;
  background:#fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%237a8a82'/%3E%3C/svg%3E") no-repeat right 7px center;
  border:1px solid rgba(30,50,38,.12);
  border-radius:8px;
  padding:5px 24px 5px 9px;
  font-size:11px;font-weight:600;
  color:#18221e;
  font-family:'IBM Plex Mono',monospace;
  cursor:pointer;outline:none;
  transition:all .15s;
  white-space:nowrap;
}
.isp-fsel:hover{border-color:rgba(181,154,91,.5);background-color:#fffdf7;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23b59a5b'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 7px center;}
.isp-fsel.active{border-color:${C.gold};color:${C.gold};background:#fffaed;font-weight:800;}
/* search input */
.isp-fsearch{
  background:#fff;
  border:1px solid rgba(30,50,38,.12);
  border-radius:8px;padding:5px 9px 5px 28px;
  font-size:11px;color:#18221e;
  font-family:'IBM Plex Mono',monospace;
  outline:none;transition:all .15s;width:110px;
}
.isp-fsearch:focus{border-color:rgba(181,154,91,.5);background:#fffdf7;}
.isp-fsearch.has-value{border-color:${C.gold};background:#fffaed;}
.isp-fsearch::placeholder{color:#a0a89e;}

/* tooltips : InvelpsTipMark (portal) — pas de pseudo-éléments coupés */
.isp-tip-inline{display:inline-flex;align-items:center;gap:3px;vertical-align:middle;}
.isp-tbl thead th,.isp-tbl-wrap{overflow:visible;}
.isp-kpi-grid > div{overflow:visible;}
/* constituents table rows */
.isp-ct tbody tr{transition:background .1s;}
.isp-ct tbody td:first-child{box-shadow:inset 3px 0 0 transparent;transition:box-shadow .12s,background .12s;}
.isp-ct tbody tr:hover td{background:#faf7ee;}
.isp-ct tbody tr:hover td:first-child{box-shadow:inset 3px 0 0 ${C.gold};}
`;

function SortIcon({ active, asc }) {
  const cls = `inv-sort-icon${active ? (asc ? " asc" : " desc") : " idle"}`;
  return (
    <span className={cls} aria-hidden>
      <i className="inv-sort-up" />
      <i className="inv-sort-down" />
    </span>
  );
}

function useCSS() {
  useEffect(() => {
    let el = document.getElementById("isp-css");
    if (!el) {
      el = document.createElement("style");
      el.id = "isp-css";
      document.head.appendChild(el);
    }
    el.textContent = CSS;
  }, []);
}

/* ─── INVESTORS ──────────────────────────────────────────────── */
const INVESTORS = {
  "Peter Lynch":         { ini:"PL",  style:"GARP",        bio:"Gérant du Magellan Fund de 1977 à 1990, +29.2%/an. Auteur de 'One Up on Wall Street' (1989).", philosophy:"GARP — Growth at a Reasonable Price. Il popularisa le ratio PEG pour identifier des croissances sous-évaluées. Investir dans ce qu'on comprend." },
  "Christopher Mayer":   { ini:"CM",  style:"Value",        bio:"Auteur de '100 Baggers' (2015). Gérant chez Woodlock House Family Capital.", philosophy:"Identifier des entreprises capables de multiplier leur valeur par 100 via un dirigeant-actionnaire aligné et la patience du compounding." },
  "Warren Buffett":      { ini:"WB",  style:"Value",        bio:"PDG de Berkshire Hathaway depuis 1965. Oracle d'Omaha. ~20%/an composé sur 60 ans.", philosophy:"Acheter de grandes entreprises à prix raisonnable et les conserver indéfiniment. Moat durable, management de grande qualité." },
  "Jim Slater":          { ini:"JS",  style:"GARP",         bio:"Investisseur britannique (1929-2015). Auteur du Zulu Principle (1992). Pionnier du ratio PEG.", philosophy:"Small caps sous-recherchées avec PEG < 1, forte croissance bénéficiaire et bilan sain." },
  "Benjamin Graham":     { ini:"BG",  style:"Value",        bio:"Père du value investing (1894-1976). 'Security Analysis' (1934), 'The Intelligent Investor' (1949). Mentor de Buffett.", philosophy:"Acheter sous la valeur intrinsèque avec une marge de sécurité. Investisseur défensif vs entreprenant." },
  "Joel Greenblatt":     { ini:"JG",  style:"Value",        bio:"Fondateur de Gotham Capital (+40%/an sur 20 ans). Professeur à Columbia. 'The Little Book That Beats the Market' (2005).", philosophy:"Magic Formula : earnings yield + ROIC pour acheter systématiquement des entreprises rentables sous-valorisées." },
  "Philip Fisher":       { ini:"PF",  style:"Growth",       bio:"Fondateur de Fisher & Company (1931-1999). 'Common Stocks and Uncommon Profits' (1958). Influence reconnue par Buffett.", philosophy:"Entreprises de croissance avec management visionnaire, R&D solide. Méthode 'scuttlebutt'." },
  "Joseph Piotroski":    { ini:"JP",  style:"Value",        bio:"Professeur à Stanford. F-Score publié en 2000 dans le Journal of Accounting Research.", philosophy:"F-Score : 9 critères binaires sur rentabilité, endettement et efficacité pour filtrer les value stocks." },
  "James O'Shaughnessy": { ini:"JO",  style:"Quantitative", bio:"Auteur de 'What Works on Wall Street' (1996). Fondateur OSAM. Pionnier de l'investissement factoriel.", philosophy:"Backtests sur 50+ ans pour identifier les facteurs persistants : shareholder yield, momentum, valeur composite." },
  "Mary Buffett":        { ini:"MB",  style:"Value",        bio:"Ancienne belle-fille de Warren Buffett. Co-auteure de 'Buffettology' (1997).", philosophy:"Durable Competitive Advantage : ROIC élevé, moat protégeant les bénéfices, croissance prévisible." },
  "Seth Klarman":        { ini:"SK",  style:"Value",        bio:"Fondateur de Baupost Group (>30Mds$). 'Margin of Safety' (1991), épuisé >1000$.", philosophy:"Marge de sécurité maximale sur actifs tangibles décotés. Protection du capital avant tout." },
  "Charlie Munger":      { ini:"CM2", style:"Value",        bio:"Vice-Président de Berkshire pendant 50 ans. Décédé en novembre 2023 à 99 ans.", philosophy:"Modèles mentaux multidisciplinaires. Payer le juste prix pour une entreprise excellente." },
  "George Soros":        { ini:"GS",  style:"Macro",        bio:"Fondateur du Quantum Fund. +1Md$ en shortant la livre sterling en 1992.", philosophy:"Réflexivité : perceptions et fondamentaux se co-construisent en boucles exploitables." },
  "Ray Dalio":           { ini:"RD",  style:"Macro",        bio:"Fondateur de Bridgewater Associates (~160Mds$). Auteur de 'Principles'.", philosophy:"All Weather Portfolio : quatre quadrants croissance/inflation avec actifs non-corrélés." },
  "Michael Burry":       { ini:"MB2", style:"Contrarian",   bio:"Fondateur de Scion Capital. Shorté les subprimes en 2007-2008 (The Big Short).", philosophy:"Deep value bottom-up rigoureux. Agir à contre-courant avec une conviction documentée." },
  "David Dreman":        { ini:"DD",  style:"Contrarian",   bio:"Fondateur de Dreman Value Management. Expert en finance comportementale.", philosophy:"Les analystes surestiment les valeurs populaires et sous-estiment les délaissées. Acheter bas P/E." },
  "Pat Dorsey":          { ini:"PD",  style:"Value",        bio:"Ex-directeur recherche Morningstar. Fondateur Dorsey Asset Management. 'The Little Book That Builds Wealth'.", philosophy:"Economic Moat : réseau, switching cost, avantages de coût, actifs intangibles." },
  "John Templeton":      { ini:"JT",  style:"Contrarian",   bio:"Pionnier de l'investissement international. Fondateur du Templeton Growth Fund en 1954.", philosophy:"Acheter au point de pessimisme maximum dans le monde entier. Diversification géographique absolue." },
  "Walter Schloss":      { ini:"WS",  style:"Value",        bio:"Élève de Graham. Gérant indépendant pendant 50 ans sans ordinateur. ~15.7%/an.", philosophy:"Acheter sous la valeur comptable, diversifier sur 100+ titres, ne jamais vendre par impatience." },
  "Howard Marks":        { ini:"HM",  style:"Value",        bio:"Co-fondateur d'Oaktree Capital (~180Mds$). 'The Most Important Thing' et 'Mastering the Market Cycle'.", philosophy:"Second-level thinking : anticiper ce que le marché anticipe déjà. Risque avant rendement." },
  "Ralph Wanger":        { ini:"RW",  style:"Growth",       bio:"Gérant du Columbia Acorn Fund de 1970 à 2003. 'A Zebra in Lion Country'.", philosophy:"Small caps de niche avec croissance bénéficiaire soutenue et management aligné." },
  "Jim Simons":          { ini:"JS2", style:"Quantitative", bio:"Fondateur de Renaissance Technologies. Medallion Fund : +66%/an brut sur 30 ans.", philosophy:"Algorithmes mathématiques sur données massives pour détecter des patterns statistiques non-aléatoires." },
  "Robin Speziale":      { ini:"RS",  style:"Quality",      bio:"Auteur de 'Market Masters' (2016) et 'Capital Compounders' (2018). Investisseur canadien.", philosophy:"Capital Compounders : entreprises capables de réinvestir leur capital à des taux élevés de manière durable." },
};
const STRAT_INV = {
  "100 Baggers":"Christopher Mayer","100 to 1":"Christopher Mayer",
  "All-Cap Value with Growth Twist":"Peter Lynch","Big Safe Dividends":"James O'Shaughnessy",
  "Buy Good Do Nothing":"Charlie Munger","Contrarian Value":"David Dreman",
  "Deep Value":"Benjamin Graham","Durable Moat":"Pat Dorsey",
  "Global Bargains Hunting":"John Templeton","Low P/E Value Investing":"Benjamin Graham",
  "Magic Formula":"Joel Greenblatt","Market Leaders Low P/CF":"James O'Shaughnessy",
  "Modern Value Investing":"Howard Marks","One Up":"Peter Lynch",
  "Only the Best":"Philip Fisher","Owning the Best":"Warren Buffett",
  "Permanent Portfolio":"Ray Dalio","Piotroski F-Score":"Joseph Piotroski",
  "Quality Growth Investing":"Peter Lynch","Rule 1":"Warren Buffett",
  "Small Cap Value & Growth":"Jim Slater","The Intelligent Investor - Defensive":"Benjamin Graham",
  "The Intelligent Investor - Enterprising":"Benjamin Graham","The Moat":"Warren Buffett",
  "The Zulu Principle":"Jim Slater","Tiny Titans":"James O'Shaughnessy",
  "Value Investing Toolbox - Core":"Benjamin Graham","Value Investing Toolbox - Value":"Walter Schloss",
  "Zebra in Lion Country":"Ralph Wanger","Capital Compounders":"Robin Speziale",
};

const StrategiesDataContext = createContext({
  stratInv: STRAT_INV,
  investors: INVESTORS,
  allS: null,
  allComp: null,
  loading: false,
  onRequestConstituents: null,
  onOpenTicker: null,
  onRequestInvestor: null,
});

function useStrategiesData() {
  return useContext(StrategiesDataContext);
}

function fmtMoVal(c){ const mo=Number(c?._mo); if(!Number.isFinite(mo)) return "—"; return mo>=0?`+${mo.toFixed(0)}%`:`${mo.toFixed(0)}%`; }
function moNum(c){ const mo=Number(c?._mo); return Number.isFinite(mo)?mo:0; }

/* ─── STRATEGY DATA PARSING ──────────────────────────────────── */
const RAW = `1458,2024,2026,100 Baggers,0.75,-0.74,-4.32,4.5,,,,,,,,,,-0.01,,-5.17,13.03,0.77,-4.93,,,,,,,,,,,,,,,,,,
1459,2024,2026,100 to 1,3.72,3.57,-5.15,7.89,,,,,,,,,,9.93,,-5.05,11.7,5.01,-7.66,,,,,,,,,,,,,,,,,,
1460,2024,2026,All-Cap Value with Growth Twist,-7.69,6.08,-2.48,4.95,,,,,,,,,,0.22,,-35.77,-2.31,-5.52,2.74,,,,,,,,,,,,,,,,,,
1461,2024,2026,Big Safe Dividends,7.43,10.19,-13.78,6.14,,,,,,,,,,8.33,,14.07,11.13,4.66,3.28,,,,,,,,,,,,,,,,,,
1462,2024,2026,Buy Good, Do Nothing,-4.17,4.01,-14.13,6.48,,,,,,,,,,-8.86,,,,,,,,,,,,,,,,,,,,,,,,,,
1463,2024,2026,Capital Compounders,1.47,0.9,12.74,-3.27,,,,,,,,,,11.65,,,,,,,,,,,,,,,,,,,,,,,,,,
1464,2024,2026,Contrarian Value,2.99,4.16,-7.42,1.59,,,,,,,,,,0.89,,23.54,14.1,1.84,9.66,,,,,,,,,,,,,,,,,,
1465,2024,2026,Deep Value,12.01,56.57,-23.28,9.15,,,,,,,,,,46.86,,22.32,9.69,0.88,9.6,,,,,,,,,,,,,,,,,,
1466,2024,2026,Durable Moat,38.91,-0.82,-10.99,57.26,,,,,,,,,,92.85,,1.1,28.59,3.85,-1.36,,,,,,,,,,,,,,,,,,
1467,2024,2026,Global Bargains Hunting,8.47,-8.47,4.45,1.81,,,,,,,,,,5.58,,16.92,18.18,8.1,15.01,,,,,,,,,,,,,,,,,,
1468,2024,2026,Low P/E Value Investing,-8.15,-1.32,-0.53,-6.69,,,,,,,,,,-15.87,,-17.52,-9.99,-19.78,-1.03,,,,,,,,,,,,,,,,,,
1469,2024,2026,Magic Formula,-4.27,-12.76,-1.57,10.18,,,,,,,,,,-9.43,,26.7,2.47,10.25,,,,,,,,,,,,,,,,,,,
1470,2024,2026,Market Leaders Low P/CF,18.86,20.32,-12.56,1.52,,,,,,,,,,26.95,,29.17,30.39,11.83,14.56,,,,,,,,,,,,,,,,,,
1471,2024,2026,Modern Value Investing,-3.41,14.21,-15.41,14.88,,,,,,,,,,7.2,,-2.04,7.06,-1.41,6.79,,,,,,,,,,,,,,,,,,
1472,2024,2026,One Up,-3.6,7.45,-12.88,3.66,,,,,,,,,,-6.46,,6.97,11.93,-9.24,12.11,,,,,,,,,,,,,,,,,,
1473,2024,2026,Owning the Best,4.48,-11.34,-9.33,15.21,,,,,,,,,,-3.24,,0.5,7.63,-3.45,1.73,,,,,,,,,,,,,,,,,,
1474,2024,2026,Permanent Portfolio,-3.65,-17.96,2.41,2.94,,,,,,,,,,-16.67,,-2.15,-7.28,-23.7,4.46,,,,,,,,,,,,,,,,,,
1475,2024,2026,Piotroski F-Score,4.58,4.66,-2.01,5.02,,,,,,,,,,12.64,,7.06,2.62,-3.71,6.75,,,,,,,,,,,,,,,,,,
1476,2024,2026,Quality Growth Investing,-4.06,3.25,-2.07,11.7,,,,,,,,,,8.36,,9.06,24.27,3.59,4.31,,,,,,,,,,,,,,,,,,
1477,2024,2026,Rule 1,16.7,26.4,-14.33,10.39,,,,,,,,,,39.5,,49.82,56.25,9.36,15.8,,,,,,,,,,,,,,,,,,
1478,2024,2026,Small Cap Value & Growth,0.9,1.98,-4.91,11.66,,,,,,,,,,9.25,,-18.21,-7.47,-20.29,-1.99,,,,,,,,,,,,,,,,,,
1479,2024,2026,The Intelligent Investor - Defensive,6.83,10.32,-14.77,8.22,,,,,,,,,,8.7,,17.52,13.79,3.33,8.81,,,,,,,,,,,,,,,,,,
1480,2024,2026,The Intelligent Investor - Enterprising,-0.52,5.12,-6.66,2.37,,,,,,,,,,-0.08,,18.47,9.94,-1.49,-3.69,,,,,,,,,,,,,,,,,,
1481,2024,2026,The Moat,-2.05,1.63,-16.83,8.29,,,,,,,,,,-10.34,,44.96,12.81,6.84,35.33,,,,,,,,,,,,,,,,,,
1482,2024,2026,The Zulu Principle,9.55,19.89,-18.53,-1.36,,,,,,,,,,5.55,,68.43,51,42.11,20.53,,,,,,,,,,,,,,,,,,
1483,2024,2026,Tiny Titans,-0.41,0.75,36.43,5.52,,,,,,,,,,44.45,,,,,,,,,,,,,,,,,,,,,,,,,
1484,2024,2026,Value Investing Toolbox - Core,1.41,10.52,-11.12,10.08,,,,,,,,,,9.66,,17.57,17.26,7.49,10.45,,,,,,,,,,,,,,,,,,
1485,2024,2026,Value Investing Toolbox - Value,1.54,10.55,-11.2,10.38,,,,,,,,,,10.03,,0.66,15.41,10.38,19,,,,,,,,,,,,,,,,,,
1486,2024,2026,Zebra in Lion Country,26.8,-5.46,-17.5,-4.44,,,,,,,,,,,-5.49,,16.21,37.48,18.65,8.43,,,,,,,,,,,,,,,,,,
1769,2024,2025,100 Baggers,6.25,9.17,-5.77,-1.8,2.85,11.76,3.14,-4.47,-0.58,1.86,0.71,-8.91,12.93,12.94,-5.17,13.03,0.77,-4.93,20.96,28.57,20.67,31.17,27.23,-11.25,-11.25,-18.18,-32.29,-35.86,,,,,,,
1770,2024,2025,100 to 1,-7.44,4.3,-5.29,9.18,11.19,13.07,-0.81,-4.77,-3.66,-3.27,5.44,-1.29,14.98,14.99,-5.05,11.7,5.01,-7.66,24.22,10.57,13.78,19.43,17.62,-11.97,-17.16,-17.16,-17.16,-29.82,,,,,,,
1771,2024,2025,All-Cap Value with Growth Twist,2.51,5.77,-6.6,8.74,10.77,-1.93,0.43,0.02,-8.97,-4.75,-6.5,-2.77,-5.28,-5.28,-35.77,-2.31,-5.52,2.74,21.7,26.65,69.59,62.21,54.67,-22.35,-22.35,-22.35,-22.35,-22.35,,,,,,,
1772,2024,2025,Big Safe Dividends,3.07,2.94,2.85,4.33,6.31,3.56,3.57,-4.19,0.93,0.31,9.7,1.35,40,40.01,14.07,11.13,4.66,3.28,11.63,33.41,24.16,20.54,18,-4.19,-14.61,-14.61,-14.61,-14.61,,,,,,,
1773,2024,2025,Buy Good, Do Nothing,-3.76,-4.84,4.49,0.88,-0.08,4.55,-0.41,3.52,-0.19,-0.41,2.78,-2.12,3.97,3.95,,,,,10.58,8.45,8.45,8.45,8.45,-4.84,-4.84,-4.84,-4.84,-4.84,,,,,,,
1774,2024,2025,Capital Compounders,0.73,-1.53,-4.26,-0.12,2.31,2.15,2.43,-2.61,0.71,2.69,-2.62,-4.6,-4.99,-5.01,,,,,9.2,6.16,6.16,6.16,6.16,-7.1,-26.11,-26.11,-26.11,-26.11,,,,,,,
1775,2024,2025,Contrarian Value,2.81,1.31,-0.15,1.15,1.34,6.56,5.49,4.24,-0.34,4.74,2.24,-1.49,31.33,31.33,23.54,14.1,1.84,9.66,8.67,26.88,24.32,21.25,20.93,-1.49,-10.9,-25.94,-25.94,-25.94,,,,,,,
1776,2024,2025,Deep Value,3.16,13.08,-6.22,11.97,27.37,2.1,1.73,-11.5,-0.6,-6.29,0.39,-4.68,27.83,27.85,22.32,9.69,0.88,9.6,36.72,33.29,44.76,40,34.66,-21.12,-21.12,-24.17,-24.17,-24.17,,,,,,,
1777,2024,2025,Durable Moat,10.6,-6.75,14.27,-8.14,-2.48,-3.32,-8.52,-5.8,14.43,19.38,-3.35,6.91,24.15,24.13,1.1,28.59,3.85,-1.36,35.14,12.96,9.9,30.46,26.45,-25.37,-25.37,-25.37,-36.59,-36.59,,,,,,,
1778,2024,2025,Global Bargains Hunting,14.63,-1.37,1.01,1.79,4.16,9.44,3.37,-0.75,2.74,7.65,2.78,-0.7,53.46,53.45,16.92,18.18,8.1,15.01,16.38,33.35,25.68,26.08,22.66,-1.37,-13.14,-13.14,-13.14,-17.06,,,,,,,
1779,2024,2025,Low P/E Value Investing,-4.07,-7.35,7.82,2.44,4.28,-47.7,2.66,-11.71,-11.1,26.16,18.56,-0.74,-35.95,-35.95,-17.52,-9.99,-19.78,-1.03,63.47,43.72,30.92,25.48,35.33,-57.86,-57.86,-57.86,-57.86,-57.86,,,,,,,
1780,2024,2025,Magic Formula,28.53,-7.54,18.33,-17.67,14.57,2.39,-9.15,-6.46,-0.22,3.27,-0.6,-2.93,14.75,14.76,26.7,2.47,10.25,,44.8,22.68,22.46,20.21,18.95,-18.4,-25.55,-25.55,-28.91,-53.06,,,,,,,
1781,2024,2025,Market Leaders Low P/CF,0.48,0.3,-1.14,3.49,23.28,8.71,11.09,10.34,-1.05,4.54,-2.8,-2.19,66.58,66.58,29.17,30.39,11.83,14.56,26.49,58.79,44.04,41.51,35.95,-4.93,-19.44,-19.44,-28.74,-28.74,,,,,,,
1782,2024,2025,Modern Value Investing,0.61,-1.65,1.41,3.67,7.16,-2.24,2.63,-2.71,5.34,-5.68,9.01,-13.83,1.56,1.55,-2.04,7.06,-1.41,6.79,21.43,4.99,3.55,11.65,10.67,-13.83,-13.83,-13.83,-13.83,-15.94,,,,,,,
1783,2024,2025,One Up,3.8,-0.56,0.66,-4.7,12.37,5.69,5.25,-0.47,-2.96,2.63,-3.75,4.43,23.32,23.34,6.97,11.93,-9.24,12.11,16.78,14.26,10.56,14.57,36.35,-4.7,-32.14,-32.14,-33.48,-33.48,,,,,,,
1784,2024,2025,Owning the Best,-4.66,-12.79,14.12,7.51,7.42,-2.53,-6.32,-3.97,7.89,-0.32,-3.12,-2.97,-2.86,-2.85,0.5,7.63,-3.45,1.73,26.41,9.8,8.29,13.58,13.82,-12.79,-12.79,-12.79,-22.1,-22.1,,,,,,,
1785,2024,2025,Permanent Portfolio,-0.44,2.8,1,-1.28,3.74,0.55,2.62,0.67,-7.87,-4.78,-5.62,1.45,-7.63,-7.62,-2.15,-7.28,-23.7,4.46,12.64,56.05,44.62,36.5,37.43,-17.2,-17.2,-17.2,-17.2,-39.7,,,,,,,
1786,2024,2025,Piotroski F-Score,-2.34,1.15,-2.37,2.38,6.35,5.7,4.46,-5.11,-2.94,0.1,2.86,5.4,15.88,15.86,7.06,2.62,-3.71,6.75,13.31,24.94,25.71,22.38,20.65,-7.9,-7.9,-15.81,-15.81,-15.81,,,,,,,
1787,2024,2025,Quality Growth Investing,-10.47,24.86,0.14,12.81,14.26,25.11,3.44,8.54,-2.31,-11.6,7.13,-14.55,60.23,60.23,9.06,24.27,3.59,4.31,45.99,21.84,16.5,31.95,28.2,-20.95,-51.27,-51.27,-51.27,-51.27,,,,,,,
1788,2024,2025,Rule 1,7.67,4.03,14.83,9.28,7.88,23.9,9.35,1.81,5.31,-2.98,-0.99,39.26,194.65,194.7,49.82,56.25,9.36,15.8,40.42,139.29,105.38,98.85,89.17,-3.94,-28.3,-28.3,-28.3,-28.3,,,,,,,
1789,2024,2025,Small Cap Value & Growth,-4.36,-2.28,-2.62,0.3,1.4,4.97,-1.85,2.69,1.66,5.37,3.98,3.88,13.31,13.32,-18.21,-7.47,-20.29,-1.99,11.28,27.17,28.67,23.42,64.27,-4.84,-9.47,-9.47,-9.47,-16.24,,,,,,,
1790,2024,2025,The Intelligent Investor - Defensive,2.23,-1.09,4.2,3.62,5.18,4.52,0.06,1.18,0.36,0.73,4.32,4.49,33.9,33.9,17.52,13.79,3.33,8.81,7.46,16.4,18.67,19.09,17.06,-1.09,-6.96,-15.34,-17.16,-17.16,,,,,,,
1791,2024,2025,The Intelligent Investor - Enterprising,5.18,1.91,-1.3,-3.33,9.56,2.57,3.35,-2.7,-7.49,2.02,-5.8,5.08,7.97,7.98,18.47,9.94,-1.49,-3.69,17.24,5.51,16.15,14.88,16.51,-13.5,-25.07,-34.79,-34.79,-34.79,,,,,,,
1792,2024,2025,The Moat,-0.59,-0.24,-0.95,6.75,17.62,27.96,-8.59,2.38,-2.42,-4.33,4.51,-4.59,37.49,37.47,44.96,12.81,6.84,35.33,35.69,50.86,41.56,34.08,30.21,-12.88,-45.18,-45.18,-45.18,-45.18,,,,,,,
1793,2024,2025,The Zulu Principle,13.07,-2.22,18.93,8.45,12.43,7.46,0.02,16.18,25.08,-2.95,14.76,16.36,224.52,224.55,68.43,51,42.11,20.53,30.35,136.53,116.71,106.68,100.17,-2.95,-5.5,-14.29,-14.29,-24.47,,,,,,,
1794,2024,2025,Tiny Titans,-7.34,-5.07,2.21,3.03,3.69,3.6,0.8,-4.6,-3.95,-2.03,-0.88,-2.95,-13.38,-13.38,,,,,13.05,17.52,17.52,17.52,17.52,-13.64,-45.01,-45.01,-45.01,-45.01,,,,,,,
1795,2024,2025,Value Investing Toolbox - Core,2.2,4.58,4.07,3.87,13.45,4.97,2.1,-0.9,1.94,-2.09,-0.43,4.32,44.33,44.32,17.57,17.26,7.49,10.45,13.79,27.51,22.76,23.43,20.33,-2.51,-6.56,-24.18,-24.18,-24.18,,,,,,,
1796,2024,2025,Value Investing Toolbox - Value,1.63,4.58,4.26,4.91,13.12,4.75,1.85,-0.51,2.73,-2.39,0.07,4.12,45.84,45.83,0.66,15.41,10.38,19,13.49,20.97,16.6,24.34,24.28,-2.39,-11.77,-18.56,-23.23,-23.23,,,,,,,
1797,2024,2025,Zebra in Lion Country,17,5.56,0.38,11.41,-0.31,-5.05,23.18,13.81,2.13,-1.41,8.43,-4.23,91.64,91.62,16.21,37.48,18.65,8.43,30.99,38.7,52.25,73.44,70.25,-5.34,-10.5,-15.23,-30.56,-30.56,,,,,,,`;

function parseS(raw) {
  return raw.trim().split("\n").filter(l=>l.trim()).map(line=>{
    const p=line.split(",").map(s=>s.trim()); const v=i=>{const n=parseFloat(p[i]);return isNaN(n)?null:n;};
    return{
      id:parseInt(p[0]), selYear:parseInt(p[1]), analYear:parseInt(p[2]), name:p[3],
      monthly:MONTHS.map((m,i)=>({m,val:v(4+i)})),
      fyM:v(16), fyA:v(17),
      c2:v(18), c3:v(19), c4:v(20), c5:v(21),
      s1:v(22), s2:v(23), s3:v(24), s4:v(25), s5:v(26),
      mdd1:v(27), mdd2:v(28), mdd3:v(29), mdd4:v(30), mdd5:v(31),
      sh1:v(32), sh2:v(33), sh3:v(34), sh4:v(35), sh5:v(36),
    };
  }).filter(d=>!isNaN(d.id));
}
const ALL_S = parseS(RAW);

/* ─── COMPOSITION DATA ───────────────────────────────────────── */
const RAW_COMP = [
  {symbol:"CPRT",market:"US",sector:"Industrials",strategy:"100 Baggers",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"POOL",market:"US",sector:"Consumer Discretionary",strategy:"100 Baggers",stock_style:"Quality",cap:"Mid Cap"},
  {symbol:"MELI",market:"US",sector:"Consumer Discretionary",strategy:"100 Baggers",stock_style:"Growth",cap:"Large Cap"},
  {symbol:"FICO",market:"US",sector:"Information Technology",strategy:"100 Baggers",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"IDXX",market:"US",sector:"Health Care",strategy:"100 Baggers",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"CSU.TO",market:"CA",sector:"Information Technology",strategy:"100 Baggers",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"BRK.B",market:"US",sector:"Financials",strategy:"100 to 1",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"MSCI",market:"US",sector:"Financials",strategy:"100 to 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"SPGI",market:"US",sector:"Financials",strategy:"100 to 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"LVMH.PA",market:"FR",sector:"Consumer Discretionary",strategy:"100 to 1",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"ITW",market:"US",sector:"Industrials",strategy:"100 to 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"AAPL",market:"US",sector:"Information Technology",strategy:"All-Cap Value with Growth Twist",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"GOOGL",market:"US",sector:"Communication Services",strategy:"All-Cap Value with Growth Twist",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"META",market:"US",sector:"Communication Services",strategy:"All-Cap Value with Growth Twist",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"ASML.AS",market:"NL",sector:"Information Technology",strategy:"All-Cap Value with Growth Twist",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"NVO",market:"DK",sector:"Health Care",strategy:"All-Cap Value with Growth Twist",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"JNJ",market:"US",sector:"Health Care",strategy:"Big Safe Dividends",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"PG",market:"US",sector:"Consumer Staples",strategy:"Big Safe Dividends",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"KO",market:"US",sector:"Consumer Staples",strategy:"Big Safe Dividends",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"T",market:"US",sector:"Communication Services",strategy:"Big Safe Dividends",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"TTE.PA",market:"FR",sector:"Energy",strategy:"Big Safe Dividends",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"MCD",market:"US",sector:"Consumer Discretionary",strategy:"Big Safe Dividends",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"AAPL",market:"US",sector:"Information Technology",strategy:"Buy Good Do Nothing",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"KO",market:"US",sector:"Consumer Staples",strategy:"Buy Good Do Nothing",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"V",market:"US",sector:"Financials",strategy:"Buy Good Do Nothing",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"COST",market:"US",sector:"Consumer Staples",strategy:"Buy Good Do Nothing",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"BTI",market:"GB",sector:"Consumer Staples",strategy:"Contrarian Value",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"INTC",market:"US",sector:"Information Technology",strategy:"Contrarian Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"PRX.AS",market:"NL",sector:"Consumer Discretionary",strategy:"Contrarian Value",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"VOW3.DE",market:"DE",sector:"Consumer Discretionary",strategy:"Contrarian Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"BNP.PA",market:"FR",sector:"Financials",strategy:"Contrarian Value",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"RIO.L",market:"GB",sector:"Materials",strategy:"Contrarian Value",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"PRX.AS",market:"NL",sector:"Consumer Discretionary",strategy:"Deep Value",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"ASML.AS",market:"NL",sector:"Information Technology",strategy:"Deep Value",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"VOW3.DE",market:"DE",sector:"Consumer Discretionary",strategy:"Deep Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"BABA",market:"CN",sector:"Consumer Discretionary",strategy:"Deep Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"INTC",market:"US",sector:"Information Technology",strategy:"Deep Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"TEVA",market:"IL",sector:"Health Care",strategy:"Deep Value",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"OR.PA",market:"FR",sector:"Consumer Staples",strategy:"Durable Moat",stock_style:"High_Quality",cap:"Mega Cap"},
  {symbol:"MSFT",market:"US",sector:"Information Technology",strategy:"Durable Moat",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"V",market:"US",sector:"Financials",strategy:"Durable Moat",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"MA",market:"US",sector:"Financials",strategy:"Durable Moat",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"COST",market:"US",sector:"Consumer Staples",strategy:"Durable Moat",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"BABA",market:"CN",sector:"Consumer Discretionary",strategy:"Global Bargains Hunting",stock_style:"Value",cap:"Large Cap"},
  {symbol:"005930.KS",market:"KR",sector:"Information Technology",strategy:"Global Bargains Hunting",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"VALE3.SA",market:"BR",sector:"Materials",strategy:"Global Bargains Hunting",stock_style:"Value",cap:"Large Cap"},
  {symbol:"SMDR.JK",market:"ID",sector:"Industrials",strategy:"Global Bargains Hunting",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"KAP.IL",market:"KZ",sector:"Energy",strategy:"Global Bargains Hunting",stock_style:"Value",cap:"Large Cap"},
  {symbol:"INTC",market:"US",sector:"Information Technology",strategy:"Low P/E Value Investing",stock_style:"Value",cap:"Large Cap"},
  {symbol:"BNP.PA",market:"FR",sector:"Financials",strategy:"Low P/E Value Investing",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"BTI",market:"GB",sector:"Consumer Staples",strategy:"Low P/E Value Investing",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"F",market:"US",sector:"Consumer Discretionary",strategy:"Low P/E Value Investing",stock_style:"Value",cap:"Large Cap"},
  {symbol:"AIR.PA",market:"FR",sector:"Industrials",strategy:"Magic Formula",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"GOOG",market:"US",sector:"Communication Services",strategy:"Magic Formula",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"META",market:"US",sector:"Communication Services",strategy:"Magic Formula",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"ORCL",market:"US",sector:"Information Technology",strategy:"Magic Formula",stock_style:"Value",cap:"Large Cap"},
  {symbol:"AMAT",market:"US",sector:"Information Technology",strategy:"Magic Formula",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"XOM",market:"US",sector:"Energy",strategy:"Market Leaders Low P/CF",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"CVX",market:"US",sector:"Energy",strategy:"Market Leaders Low P/CF",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"JPM",market:"US",sector:"Financials",strategy:"Market Leaders Low P/CF",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"RIO.L",market:"GB",sector:"Materials",strategy:"Market Leaders Low P/CF",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"MKL",market:"US",sector:"Financials",strategy:"Modern Value Investing",stock_style:"Value",cap:"Large Cap"},
  {symbol:"LEN",market:"US",sector:"Consumer Discretionary",strategy:"Modern Value Investing",stock_style:"Value",cap:"Large Cap"},
  {symbol:"OMC",market:"US",sector:"Communication Services",strategy:"Modern Value Investing",stock_style:"Value",cap:"Large Cap"},
  {symbol:"SBUX",market:"US",sector:"Consumer Discretionary",strategy:"One Up",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"CMG",market:"US",sector:"Consumer Discretionary",strategy:"One Up",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"LULU",market:"CA",sector:"Consumer Discretionary",strategy:"One Up",stock_style:"Growth",cap:"Large Cap"},
  {symbol:"MNST",market:"US",sector:"Consumer Staples",strategy:"One Up",stock_style:"Growth",cap:"Large Cap"},
  {symbol:"AZO",market:"US",sector:"Consumer Discretionary",strategy:"One Up",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"MSFT",market:"US",sector:"Information Technology",strategy:"Only the Best",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"NVDA",market:"US",sector:"Information Technology",strategy:"Only the Best",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"LLY",market:"US",sector:"Health Care",strategy:"Only the Best",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"NOW",market:"US",sector:"Information Technology",strategy:"Only the Best",stock_style:"Growth",cap:"Large Cap"},
  {symbol:"AAPL",market:"US",sector:"Information Technology",strategy:"Owning the Best",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"KO",market:"US",sector:"Consumer Staples",strategy:"Owning the Best",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"AXP",market:"US",sector:"Financials",strategy:"Owning the Best",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"OXY",market:"US",sector:"Energy",strategy:"Owning the Best",stock_style:"Value",cap:"Large Cap"},
  {symbol:"GLD",market:"US",sector:"Materials",strategy:"Permanent Portfolio",stock_style:"Core",cap:"Large Cap"},
  {symbol:"TLT",market:"US",sector:"Financials",strategy:"Permanent Portfolio",stock_style:"Income",cap:"Large Cap"},
  {symbol:"SPY",market:"US",sector:"Financials",strategy:"Permanent Portfolio",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"VNQ",market:"US",sector:"Real Estate",strategy:"Permanent Portfolio",stock_style:"Income",cap:"Large Cap"},
  {symbol:"SAN.PA",market:"FR",sector:"Health Care",strategy:"Piotroski F-Score",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"KAP.IL",market:"KZ",sector:"Energy",strategy:"Piotroski F-Score",stock_style:"Value",cap:"Large Cap"},
  {symbol:"NICL.JK",market:"ID",sector:"Materials",strategy:"Piotroski F-Score",stock_style:"Value",cap:"Nano Cap"},
  {symbol:"XOM",market:"US",sector:"Energy",strategy:"Piotroski F-Score",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"NUE",market:"US",sector:"Materials",strategy:"Piotroski F-Score",stock_style:"Value",cap:"Large Cap"},
  {symbol:"MC.PA",market:"FR",sector:"Consumer Discretionary",strategy:"Quality Growth Investing",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"NVDA",market:"US",sector:"Information Technology",strategy:"Quality Growth Investing",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"MSFT",market:"US",sector:"Information Technology",strategy:"Quality Growth Investing",stock_style:"High_Quality",cap:"Mega Cap"},
  {symbol:"LLY",market:"US",sector:"Health Care",strategy:"Quality Growth Investing",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"ASML.AS",market:"NL",sector:"Information Technology",strategy:"Quality Growth Investing",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"AAPL",market:"US",sector:"Information Technology",strategy:"Rule 1",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"V",market:"US",sector:"Financials",strategy:"Rule 1",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"COST",market:"US",sector:"Consumer Staples",strategy:"Rule 1",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"ODFL",market:"US",sector:"Industrials",strategy:"Rule 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"CMCX.L",market:"GB",sector:"Financials",strategy:"Small Cap Value & Growth",stock_style:"Value",cap:"Small Cap"},
  {symbol:"KAPE.L",market:"GB",sector:"Information Technology",strategy:"Small Cap Value & Growth",stock_style:"Growth",cap:"Small Cap"},
  {symbol:"NICL.JK",market:"ID",sector:"Materials",strategy:"Small Cap Value & Growth",stock_style:"Value",cap:"Nano Cap"},
  {symbol:"ONEGLOBAL.BO",market:"IN",sector:"Industrials",strategy:"Small Cap Value & Growth",stock_style:"Quality",cap:"Nano Cap"},
  {symbol:"002810.KS",market:"PK",sector:"Industrials",strategy:"The Intelligent Investor - Defensive",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"7510.T",market:"JP",sector:"Information Technology",strategy:"The Intelligent Investor - Defensive",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"025530.KS",market:"PK",sector:"Consumer Discretionary",strategy:"The Intelligent Investor - Defensive",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"9068.T",market:"JP",sector:"Industrials",strategy:"The Intelligent Investor - Defensive",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"004370.KS",market:"PK",sector:"Consumer Staples",strategy:"The Intelligent Investor - Defensive",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"5921.T",market:"JP",sector:"Materials",strategy:"The Intelligent Investor - Defensive",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"051600.KS",market:"PK",sector:"Industrials",strategy:"The Intelligent Investor - Defensive",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"014830.KS",market:"PK",sector:"Materials",strategy:"The Intelligent Investor - Defensive",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"012330.KS",market:"PK",sector:"Consumer Discretionary",strategy:"The Intelligent Investor - Defensive",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"1925.T",market:"JP",sector:"Real Estate",strategy:"The Intelligent Investor - Defensive",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"003650.KS",market:"PK",sector:"Energy",strategy:"The Intelligent Investor - Defensive",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"1766.T",market:"JP",sector:"Real Estate",strategy:"The Intelligent Investor - Defensive",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"INDF.JK",market:"ID",sector:"Consumer Staples",strategy:"The Intelligent Investor - Defensive",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"092230.KS",market:"PK",sector:"Industrials",strategy:"The Intelligent Investor - Defensive",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"036530.KS",market:"PK",sector:"Industrials",strategy:"The Intelligent Investor - Defensive",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"100120.KQ",market:"KR",sector:"Health Care",strategy:"The Intelligent Investor - Defensive",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"049070.KQ",market:"KR",sector:"Information Technology",strategy:"The Intelligent Investor - Defensive",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"NWL",market:"US",sector:"Consumer Discretionary",strategy:"The Intelligent Investor - Enterprising",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"INTC",market:"US",sector:"Information Technology",strategy:"The Intelligent Investor - Enterprising",stock_style:"Value",cap:"Large Cap"},
  {symbol:"BTI",market:"GB",sector:"Consumer Staples",strategy:"The Intelligent Investor - Enterprising",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"T",market:"US",sector:"Communication Services",strategy:"The Intelligent Investor - Enterprising",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"PRX.AS",market:"NL",sector:"Consumer Discretionary",strategy:"The Intelligent Investor - Enterprising",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"AAPL",market:"US",sector:"Information Technology",strategy:"The Moat",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"MSFT",market:"US",sector:"Information Technology",strategy:"The Moat",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"V",market:"US",sector:"Financials",strategy:"The Moat",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"LVMH.PA",market:"FR",sector:"Consumer Discretionary",strategy:"The Moat",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"ASML.AS",market:"NL",sector:"Information Technology",strategy:"The Moat",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"COST",market:"US",sector:"Consumer Staples",strategy:"The Moat",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"ZEBRA1",market:"AU",sector:"Materials",strategy:"The Zulu Principle",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"KAPE.L",market:"GB",sector:"Information Technology",strategy:"The Zulu Principle",stock_style:"Growth",cap:"Small Cap"},
  {symbol:"AJB.L",market:"GB",sector:"Financials",strategy:"The Zulu Principle",stock_style:"Growth",cap:"Small Cap"},
  {symbol:"SDI.L",market:"GB",sector:"Industrials",strategy:"The Zulu Principle",stock_style:"Growth",cap:"Small Cap"},
  {symbol:"ONEGLOBAL.BO",market:"IN",sector:"Industrials",strategy:"Tiny Titans",stock_style:"Quality",cap:"Nano Cap"},
  {symbol:"NICL.JK",market:"ID",sector:"Materials",strategy:"Tiny Titans",stock_style:"Value",cap:"Nano Cap"},
  {symbol:"7191.T",market:"JP",sector:"Financials",strategy:"Tiny Titans",stock_style:"Growth",cap:"Small Cap"},
  {symbol:"SDI.L",market:"GB",sector:"Industrials",strategy:"Tiny Titans",stock_style:"Growth",cap:"Small Cap"},
  {symbol:"JNJ",market:"US",sector:"Health Care",strategy:"Value Investing Toolbox - Core",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"KO",market:"US",sector:"Consumer Staples",strategy:"Value Investing Toolbox - Core",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"T",market:"US",sector:"Communication Services",strategy:"Value Investing Toolbox - Core",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"BNP.PA",market:"FR",sector:"Financials",strategy:"Value Investing Toolbox - Core",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"INTC",market:"US",sector:"Information Technology",strategy:"Value Investing Toolbox - Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"F",market:"US",sector:"Consumer Discretionary",strategy:"Value Investing Toolbox - Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"BABA",market:"CN",sector:"Consumer Discretionary",strategy:"Value Investing Toolbox - Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"BTI",market:"GB",sector:"Consumer Staples",strategy:"Value Investing Toolbox - Value",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"CPRT",market:"US",sector:"Industrials",strategy:"Zebra in Lion Country",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"KAPE.L",market:"GB",sector:"Information Technology",strategy:"Zebra in Lion Country",stock_style:"Growth",cap:"Small Cap"},
  {symbol:"SDI.L",market:"GB",sector:"Industrials",strategy:"Zebra in Lion Country",stock_style:"Growth",cap:"Small Cap"},
  {symbol:"ONEGLOBAL.BO",market:"IN",sector:"Industrials",strategy:"Zebra in Lion Country",stock_style:"Quality",cap:"Nano Cap"},
  {symbol:"TOU.TO",market:"CA",sector:"Energy",strategy:"Capital Compounders",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"CSU.TO",market:"CA",sector:"Information Technology",strategy:"Capital Compounders",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"LULU",market:"CA",sector:"Consumer Discretionary",strategy:"Capital Compounders",stock_style:"Growth",cap:"Large Cap"},
  {symbol:"ATD.TO",market:"CA",sector:"Consumer Discretionary",strategy:"Capital Compounders",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"CNR.TO",market:"CA",sector:"Industrials",strategy:"Capital Compounders",stock_style:"Quality",cap:"Large Cap"},

  /* ── extras to reach 20+ per strategy for pagination demo ── */
  {symbol:"WSO",market:"US",sector:"Industrials",strategy:"100 Baggers",stock_style:"Quality",cap:"Mid Cap"},
  {symbol:"TXRH",market:"US",sector:"Consumer Discretionary",strategy:"100 Baggers",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"DSGX.TO",market:"CA",sector:"Information Technology",strategy:"100 Baggers",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"NVR",market:"US",sector:"Consumer Discretionary",strategy:"100 Baggers",stock_style:"Quality",cap:"Mid Cap"},
  {symbol:"MNST",market:"US",sector:"Consumer Staples",strategy:"100 Baggers",stock_style:"Growth",cap:"Large Cap"},
  {symbol:"EVO.ST",market:"SE",sector:"Consumer Discretionary",strategy:"100 Baggers",stock_style:"Growth",cap:"Large Cap"},
  {symbol:"POOL",market:"US",sector:"Consumer Discretionary",strategy:"100 Baggers",stock_style:"Quality",cap:"Mid Cap"},
  {symbol:"ODFL",market:"US",sector:"Industrials",strategy:"100 Baggers",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"ADYEN.AS",market:"NL",sector:"Information Technology",strategy:"100 to 1",stock_style:"Growth",cap:"Large Cap"},
  {symbol:"AMP",market:"US",sector:"Financials",strategy:"100 to 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"ROP",market:"US",sector:"Information Technology",strategy:"100 to 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"RACE",market:"IT",sector:"Consumer Discretionary",strategy:"100 to 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"ITW",market:"US",sector:"Industrials",strategy:"100 to 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"MCO",market:"US",sector:"Financials",strategy:"100 to 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"SPGI",market:"US",sector:"Financials",strategy:"100 to 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"MSCI",market:"US",sector:"Financials",strategy:"100 to 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"SAP.DE",market:"DE",sector:"Information Technology",strategy:"All-Cap Value with Growth Twist",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"AMZN",market:"US",sector:"Consumer Discretionary",strategy:"All-Cap Value with Growth Twist",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"META",market:"US",sector:"Communication Services",strategy:"All-Cap Value with Growth Twist",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"MSTR",market:"US",sector:"Information Technology",strategy:"All-Cap Value with Growth Twist",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"DDOG",market:"US",sector:"Information Technology",strategy:"All-Cap Value with Growth Twist",stock_style:"Growth",cap:"Large Cap"},
  {symbol:"SFM",market:"US",sector:"Consumer Staples",strategy:"All-Cap Value with Growth Twist",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"SPOT",market:"SE",sector:"Communication Services",strategy:"All-Cap Value with Growth Twist",stock_style:"Growth",cap:"Large Cap"},
  {symbol:"NOW",market:"US",sector:"Information Technology",strategy:"All-Cap Value with Growth Twist",stock_style:"Growth",cap:"Large Cap"},
  {symbol:"JNJ",market:"US",sector:"Health Care",strategy:"Big Safe Dividends",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"VZ",market:"US",sector:"Communication Services",strategy:"Big Safe Dividends",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"IBM",market:"US",sector:"Information Technology",strategy:"Big Safe Dividends",stock_style:"Income",cap:"Large Cap"},
  {symbol:"ULVR.L",market:"GB",sector:"Consumer Staples",strategy:"Big Safe Dividends",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"GSK.L",market:"GB",sector:"Health Care",strategy:"Big Safe Dividends",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"ENB.TO",market:"CA",sector:"Energy",strategy:"Big Safe Dividends",stock_style:"Income",cap:"Large Cap"},
  {symbol:"MMM",market:"US",sector:"Industrials",strategy:"Big Safe Dividends",stock_style:"Income",cap:"Large Cap"},
  {symbol:"ABBV",market:"US",sector:"Health Care",strategy:"Big Safe Dividends",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"AXP",market:"US",sector:"Financials",strategy:"Buy Good Do Nothing",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"MCO",market:"US",sector:"Financials",strategy:"Buy Good Do Nothing",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"BRK.B",market:"US",sector:"Financials",strategy:"Buy Good Do Nothing",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"LUV",market:"US",sector:"Industrials",strategy:"Buy Good Do Nothing",stock_style:"Value",cap:"Large Cap"},
  {symbol:"LVMH.PA",market:"FR",sector:"Consumer Discretionary",strategy:"Buy Good Do Nothing",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"OR.PA",market:"FR",sector:"Consumer Staples",strategy:"Buy Good Do Nothing",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"WMT",market:"US",sector:"Consumer Staples",strategy:"Buy Good Do Nothing",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"MSFT",market:"US",sector:"Information Technology",strategy:"Buy Good Do Nothing",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"BTI",market:"GB",sector:"Consumer Staples",strategy:"Contrarian Value",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"MPW",market:"US",sector:"Real Estate",strategy:"Contrarian Value",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"CVS",market:"US",sector:"Health Care",strategy:"Contrarian Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"WBA",market:"US",sector:"Consumer Staples",strategy:"Contrarian Value",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"VTRS",market:"US",sector:"Health Care",strategy:"Contrarian Value",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"BMW.DE",market:"DE",sector:"Consumer Discretionary",strategy:"Contrarian Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"RNO.PA",market:"FR",sector:"Consumer Discretionary",strategy:"Contrarian Value",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"T",market:"US",sector:"Communication Services",strategy:"Contrarian Value",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"GLEN.L",market:"GB",sector:"Materials",strategy:"Contrarian Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"BMW.DE",market:"DE",sector:"Consumer Discretionary",strategy:"Deep Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"RNO.PA",market:"FR",sector:"Consumer Discretionary",strategy:"Deep Value",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"JD",market:"CN",sector:"Consumer Discretionary",strategy:"Deep Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"RIO.L",market:"GB",sector:"Materials",strategy:"Deep Value",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"CMCX.L",market:"GB",sector:"Financials",strategy:"Deep Value",stock_style:"Value",cap:"Small Cap"},
  {symbol:"TEVA",market:"IL",sector:"Health Care",strategy:"Deep Value",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"WBA",market:"US",sector:"Consumer Staples",strategy:"Deep Value",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"CVS",market:"US",sector:"Health Care",strategy:"Deep Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"F",market:"US",sector:"Consumer Discretionary",strategy:"Deep Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"VALE3.SA",market:"BR",sector:"Materials",strategy:"Deep Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"NKE",market:"US",sector:"Consumer Discretionary",strategy:"Durable Moat",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"MA",market:"US",sector:"Financials",strategy:"Durable Moat",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"SEIC",market:"US",sector:"Financials",strategy:"Durable Moat",stock_style:"High_Quality",cap:"Large Cap"},
  {symbol:"TGS",market:"US",sector:"Energy",strategy:"Durable Moat",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"LVMH.PA",market:"FR",sector:"Consumer Discretionary",strategy:"Durable Moat",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"CPRT",market:"US",sector:"Industrials",strategy:"Durable Moat",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"POOL",market:"US",sector:"Consumer Discretionary",strategy:"Durable Moat",stock_style:"Quality",cap:"Mid Cap"},
  {symbol:"FICO",market:"US",sector:"Information Technology",strategy:"Durable Moat",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"RELIANCE.NS",market:"IN",sector:"Energy",strategy:"Global Bargains Hunting",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"PETROBRAS.SA",market:"BR",sector:"Energy",strategy:"Global Bargains Hunting",stock_style:"Value",cap:"Large Cap"},
  {symbol:"RNO.PA",market:"FR",sector:"Consumer Discretionary",strategy:"Global Bargains Hunting",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"SMDR.JK",market:"ID",sector:"Industrials",strategy:"Global Bargains Hunting",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"INDF.JK",market:"ID",sector:"Consumer Staples",strategy:"Global Bargains Hunting",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"PRX.AS",market:"NL",sector:"Consumer Discretionary",strategy:"Global Bargains Hunting",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"PKX",market:"KR",sector:"Materials",strategy:"Global Bargains Hunting",stock_style:"Value",cap:"Large Cap"},
  {symbol:"T",market:"US",sector:"Communication Services",strategy:"Low P/E Value Investing",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"SAN.PA",market:"FR",sector:"Health Care",strategy:"Low P/E Value Investing",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"GM",market:"US",sector:"Consumer Discretionary",strategy:"Low P/E Value Investing",stock_style:"Value",cap:"Large Cap"},
  {symbol:"AZN.L",market:"GB",sector:"Health Care",strategy:"Low P/E Value Investing",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"MU",market:"US",sector:"Information Technology",strategy:"Low P/E Value Investing",stock_style:"Value",cap:"Large Cap"},
  {symbol:"VOW3.DE",market:"DE",sector:"Consumer Discretionary",strategy:"Low P/E Value Investing",stock_style:"Value",cap:"Large Cap"},
  {symbol:"BTI",market:"GB",sector:"Consumer Staples",strategy:"Low P/E Value Investing",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"ORCL",market:"US",sector:"Information Technology",strategy:"Magic Formula",stock_style:"Value",cap:"Large Cap"},
  {symbol:"AMAT",market:"US",sector:"Information Technology",strategy:"Magic Formula",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"LRCX",market:"US",sector:"Information Technology",strategy:"Magic Formula",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"NVS",market:"CH",sector:"Health Care",strategy:"Magic Formula",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"APH",market:"US",sector:"Information Technology",strategy:"Magic Formula",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"KLAC",market:"US",sector:"Information Technology",strategy:"Magic Formula",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"MCHP",market:"US",sector:"Information Technology",strategy:"Magic Formula",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"TXN",market:"US",sector:"Information Technology",strategy:"Magic Formula",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"XOM",market:"US",sector:"Energy",strategy:"Market Leaders Low P/CF",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"BAC",market:"US",sector:"Financials",strategy:"Market Leaders Low P/CF",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"BHP.AX",market:"AU",sector:"Materials",strategy:"Market Leaders Low P/CF",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"BP.L",market:"GB",sector:"Energy",strategy:"Market Leaders Low P/CF",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"MRK",market:"US",sector:"Health Care",strategy:"Market Leaders Low P/CF",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"ABBV",market:"US",sector:"Health Care",strategy:"Market Leaders Low P/CF",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"WFC",market:"US",sector:"Financials",strategy:"Market Leaders Low P/CF",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"C",market:"US",sector:"Financials",strategy:"Market Leaders Low P/CF",stock_style:"Value",cap:"Large Cap"},
  {symbol:"LNC",market:"US",sector:"Financials",strategy:"Modern Value Investing",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"LBTYA",market:"US",sector:"Communication Services",strategy:"Modern Value Investing",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"MET",market:"US",sector:"Financials",strategy:"Modern Value Investing",stock_style:"Value",cap:"Large Cap"},
  {symbol:"PRU",market:"US",sector:"Financials",strategy:"Modern Value Investing",stock_style:"Value",cap:"Large Cap"},
  {symbol:"PHM",market:"US",sector:"Consumer Discretionary",strategy:"Modern Value Investing",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"TOL",market:"US",sector:"Consumer Discretionary",strategy:"Modern Value Investing",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"AIG",market:"US",sector:"Financials",strategy:"Modern Value Investing",stock_style:"Value",cap:"Large Cap"},
  {symbol:"NWL",market:"US",sector:"Consumer Discretionary",strategy:"Modern Value Investing",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"DRI",market:"US",sector:"Consumer Discretionary",strategy:"One Up",stock_style:"GARP",cap:"Mid Cap"},
  {symbol:"YUM",market:"US",sector:"Consumer Discretionary",strategy:"One Up",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"EL",market:"US",sector:"Consumer Staples",strategy:"One Up",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"TJX",market:"US",sector:"Consumer Discretionary",strategy:"One Up",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"DLTR",market:"US",sector:"Consumer Discretionary",strategy:"One Up",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"AZO",market:"US",sector:"Consumer Discretionary",strategy:"One Up",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"ORLY",market:"US",sector:"Consumer Discretionary",strategy:"One Up",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"ULTA",market:"US",sector:"Consumer Discretionary",strategy:"One Up",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"AVGO",market:"US",sector:"Information Technology",strategy:"Only the Best",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"LLY",market:"US",sector:"Health Care",strategy:"Only the Best",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"UNH",market:"US",sector:"Health Care",strategy:"Only the Best",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"TMO",market:"US",sector:"Health Care",strategy:"Only the Best",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"INTU",market:"US",sector:"Information Technology",strategy:"Only the Best",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"CRM",market:"US",sector:"Information Technology",strategy:"Only the Best",stock_style:"Growth",cap:"Large Cap"},
  {symbol:"ASML.AS",market:"NL",sector:"Information Technology",strategy:"Only the Best",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"BAC",market:"US",sector:"Financials",strategy:"Owning the Best",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"CVX",market:"US",sector:"Energy",strategy:"Owning the Best",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"MCO",market:"US",sector:"Financials",strategy:"Owning the Best",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"ULVR.L",market:"GB",sector:"Consumer Staples",strategy:"Owning the Best",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"LVMH.PA",market:"FR",sector:"Consumer Discretionary",strategy:"Owning the Best",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"MA",market:"US",sector:"Financials",strategy:"Owning the Best",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"DEO.L",market:"GB",sector:"Consumer Staples",strategy:"Owning the Best",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"SHY",market:"US",sector:"Financials",strategy:"Permanent Portfolio",stock_style:"Income",cap:"Large Cap"},
  {symbol:"PDBC",market:"US",sector:"Materials",strategy:"Permanent Portfolio",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"IAU",market:"US",sector:"Materials",strategy:"Permanent Portfolio",stock_style:"Core",cap:"Large Cap"},
  {symbol:"BIL",market:"US",sector:"Financials",strategy:"Permanent Portfolio",stock_style:"Income",cap:"Large Cap"},
  {symbol:"TIPS",market:"US",sector:"Financials",strategy:"Permanent Portfolio",stock_style:"Income",cap:"Large Cap"},
  {symbol:"VEA",market:"US",sector:"Financials",strategy:"Permanent Portfolio",stock_style:"Core",cap:"Large Cap"},
  {symbol:"BLTA.JK",market:"ID",sector:"Industrials",strategy:"Piotroski F-Score",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"NICL.JK",market:"ID",sector:"Materials",strategy:"Piotroski F-Score",stock_style:"Value",cap:"Nano Cap"},
  {symbol:"MT.AS",market:"LU",sector:"Materials",strategy:"Piotroski F-Score",stock_style:"Value",cap:"Large Cap"},
  {symbol:"PKX",market:"KR",sector:"Materials",strategy:"Piotroski F-Score",stock_style:"Value",cap:"Large Cap"},
  {symbol:"LYB",market:"US",sector:"Materials",strategy:"Piotroski F-Score",stock_style:"Value",cap:"Large Cap"},
  {symbol:"MU",market:"US",sector:"Information Technology",strategy:"Piotroski F-Score",stock_style:"Value",cap:"Large Cap"},
  {symbol:"CVX",market:"US",sector:"Energy",strategy:"Piotroski F-Score",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"GOOGL",market:"US",sector:"Communication Services",strategy:"Quality Growth Investing",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"AAPL",market:"US",sector:"Information Technology",strategy:"Quality Growth Investing",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"AMZN",market:"US",sector:"Consumer Discretionary",strategy:"Quality Growth Investing",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"NVO",market:"DK",sector:"Health Care",strategy:"Quality Growth Investing",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"ADYEN.AS",market:"NL",sector:"Information Technology",strategy:"Quality Growth Investing",stock_style:"Growth",cap:"Large Cap"},
  {symbol:"SPOT",market:"SE",sector:"Communication Services",strategy:"Quality Growth Investing",stock_style:"Growth",cap:"Large Cap"},
  {symbol:"ISRG",market:"US",sector:"Health Care",strategy:"Rule 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"ROPER",market:"US",sector:"Information Technology",strategy:"Rule 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"LULU",market:"CA",sector:"Consumer Discretionary",strategy:"Rule 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"CPRT",market:"US",sector:"Industrials",strategy:"Rule 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"MA",market:"US",sector:"Financials",strategy:"Rule 1",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"NKE",market:"US",sector:"Consumer Discretionary",strategy:"Rule 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"MSFT",market:"US",sector:"Information Technology",strategy:"Rule 1",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"AJB.L",market:"GB",sector:"Financials",strategy:"Small Cap Value & Growth",stock_style:"Growth",cap:"Small Cap"},
  {symbol:"IGR.L",market:"GB",sector:"Industrials",strategy:"Small Cap Value & Growth",stock_style:"Value",cap:"Small Cap"},
  {symbol:"CMCX.L",market:"GB",sector:"Financials",strategy:"Small Cap Value & Growth",stock_style:"Value",cap:"Small Cap"},
  {symbol:"7191.T",market:"JP",sector:"Financials",strategy:"Small Cap Value & Growth",stock_style:"Growth",cap:"Small Cap"},
  {symbol:"SDI.L",market:"GB",sector:"Industrials",strategy:"Small Cap Value & Growth",stock_style:"Growth",cap:"Small Cap"},
  {symbol:"KAPE.L",market:"GB",sector:"Information Technology",strategy:"Small Cap Value & Growth",stock_style:"Growth",cap:"Small Cap"},
  {symbol:"ABEV3.SA",market:"BR",sector:"Consumer Staples",strategy:"Small Cap Value & Growth",stock_style:"Value",cap:"Small Cap"},
  {symbol:"NWL",market:"US",sector:"Consumer Discretionary",strategy:"The Intelligent Investor - Enterprising",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"VZ",market:"US",sector:"Communication Services",strategy:"The Intelligent Investor - Enterprising",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"LNC",market:"US",sector:"Financials",strategy:"The Intelligent Investor - Enterprising",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"MET",market:"US",sector:"Financials",strategy:"The Intelligent Investor - Enterprising",stock_style:"Value",cap:"Large Cap"},
  {symbol:"PRU",market:"US",sector:"Financials",strategy:"The Intelligent Investor - Enterprising",stock_style:"Value",cap:"Large Cap"},
  {symbol:"WBA",market:"US",sector:"Consumer Staples",strategy:"The Intelligent Investor - Enterprising",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"CVS",market:"US",sector:"Health Care",strategy:"The Intelligent Investor - Enterprising",stock_style:"Value",cap:"Large Cap"},
  {symbol:"MA",market:"US",sector:"Financials",strategy:"The Moat",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"OR.PA",market:"FR",sector:"Consumer Staples",strategy:"The Moat",stock_style:"Quality",cap:"Mega Cap"},
  {symbol:"NKE",market:"US",sector:"Consumer Discretionary",strategy:"The Moat",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"DEO.L",market:"GB",sector:"Consumer Staples",strategy:"The Moat",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"MELI",market:"US",sector:"Consumer Discretionary",strategy:"The Moat",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FDM.L",market:"GB",sector:"Information Technology",strategy:"The Zulu Principle",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"BVIC.L",market:"GB",sector:"Consumer Staples",strategy:"The Zulu Principle",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"SDI.L",market:"GB",sector:"Industrials",strategy:"The Zulu Principle",stock_style:"Growth",cap:"Small Cap"},
  {symbol:"IGR.L",market:"GB",sector:"Industrials",strategy:"The Zulu Principle",stock_style:"Value",cap:"Small Cap"},
  {symbol:"CMCX.L",market:"GB",sector:"Financials",strategy:"The Zulu Principle",stock_style:"Value",cap:"Small Cap"},
  {symbol:"7191.T",market:"JP",sector:"Financials",strategy:"The Zulu Principle",stock_style:"Growth",cap:"Small Cap"},
  {symbol:"ABEV3.SA",market:"BR",sector:"Consumer Staples",strategy:"Tiny Titans",stock_style:"Value",cap:"Small Cap"},
  {symbol:"DSGX.TO",market:"CA",sector:"Information Technology",strategy:"Tiny Titans",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"BLTA.JK",market:"ID",sector:"Industrials",strategy:"Tiny Titans",stock_style:"Value",cap:"Nano Cap"},
  {symbol:"KAPE.L",market:"GB",sector:"Information Technology",strategy:"Tiny Titans",stock_style:"Growth",cap:"Small Cap"},
  {symbol:"ONEGLOBAL.BO",market:"IN",sector:"Industrials",strategy:"Tiny Titans",stock_style:"Quality",cap:"Nano Cap"},
  {symbol:"PFE",market:"US",sector:"Health Care",strategy:"Value Investing Toolbox - Core",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"ABBV",market:"US",sector:"Health Care",strategy:"Value Investing Toolbox - Core",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"VZ",market:"US",sector:"Communication Services",strategy:"Value Investing Toolbox - Core",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"PG",market:"US",sector:"Consumer Staples",strategy:"Value Investing Toolbox - Core",stock_style:"Income",cap:"Mega Cap"},
  {symbol:"IBM",market:"US",sector:"Information Technology",strategy:"Value Investing Toolbox - Core",stock_style:"Value",cap:"Large Cap"},
  {symbol:"SAN.PA",market:"FR",sector:"Health Care",strategy:"Value Investing Toolbox - Core",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"MMM",market:"US",sector:"Industrials",strategy:"Value Investing Toolbox - Core",stock_style:"Income",cap:"Large Cap"},
  {symbol:"F",market:"US",sector:"Consumer Discretionary",strategy:"Value Investing Toolbox - Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"GM",market:"US",sector:"Consumer Discretionary",strategy:"Value Investing Toolbox - Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"RIO.L",market:"GB",sector:"Materials",strategy:"Value Investing Toolbox - Value",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"GLEN.L",market:"GB",sector:"Materials",strategy:"Value Investing Toolbox - Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"VOW3.DE",market:"DE",sector:"Consumer Discretionary",strategy:"Value Investing Toolbox - Value",stock_style:"Value",cap:"Large Cap"},
  {symbol:"MPW",market:"US",sector:"Real Estate",strategy:"Value Investing Toolbox - Value",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"TEVA",market:"IL",sector:"Health Care",strategy:"Value Investing Toolbox - Value",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"POOL",market:"US",sector:"Consumer Discretionary",strategy:"Zebra in Lion Country",stock_style:"Quality",cap:"Mid Cap"},
  {symbol:"DSGX.TO",market:"CA",sector:"Information Technology",strategy:"Zebra in Lion Country",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"7191.T",market:"JP",sector:"Financials",strategy:"Zebra in Lion Country",stock_style:"Growth",cap:"Small Cap"},
  {symbol:"WSO",market:"US",sector:"Industrials",strategy:"Zebra in Lion Country",stock_style:"Quality",cap:"Mid Cap"},
  {symbol:"ABEV3.SA",market:"BR",sector:"Consumer Staples",strategy:"Zebra in Lion Country",stock_style:"Value",cap:"Small Cap"},
  {symbol:"BVIC.L",market:"GB",sector:"Consumer Staples",strategy:"Zebra in Lion Country",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"WCN.TO",market:"CA",sector:"Industrials",strategy:"Capital Compounders",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"TFII.TO",market:"CA",sector:"Industrials",strategy:"Capital Compounders",stock_style:"Quality",cap:"Mid Cap"},
  {symbol:"MRU.TO",market:"CA",sector:"Consumer Staples",strategy:"Capital Compounders",stock_style:"Quality",cap:"Mid Cap"},
  {symbol:"BYD.TO",market:"CA",sector:"Industrials",strategy:"Capital Compounders",stock_style:"Quality",cap:"Mid Cap"},
  {symbol:"FSV.TO",market:"CA",sector:"Real Estate",strategy:"Capital Compounders",stock_style:"Quality",cap:"Mid Cap"},
  {symbol:"WSP.TO",market:"CA",sector:"Industrials",strategy:"Capital Compounders",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"GFL.TO",market:"CA",sector:"Industrials",strategy:"Capital Compounders",stock_style:"Growth",cap:"Mid Cap"},

  /* ── fillers for pagination demo ── */
  {symbol:"FILL001.X",market:"US",sector:"Industrials",strategy:"100 Baggers",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL002.X",market:"GB",sector:"Information Technology",strategy:"100 Baggers",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL003.X",market:"DE",sector:"Financials",strategy:"100 Baggers",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL004.X",market:"FR",sector:"Consumer Discretionary",strategy:"100 Baggers",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL005.X",market:"JP",sector:"Health Care",strategy:"100 Baggers",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL006.X",market:"CA",sector:"Materials",strategy:"100 Baggers",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL007.X",market:"AU",sector:"Energy",strategy:"100 to 1",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL008.X",market:"SE",sector:"Consumer Staples",strategy:"100 to 1",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL009.X",market:"CH",sector:"Communication Services",strategy:"100 to 1",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL010.X",market:"NL",sector:"Industrials",strategy:"100 to 1",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL011.X",market:"BR",sector:"Information Technology",strategy:"100 to 1",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL012.X",market:"KR",sector:"Financials",strategy:"100 to 1",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL013.X",market:"US",sector:"Consumer Discretionary",strategy:"100 to 1",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL014.X",market:"GB",sector:"Health Care",strategy:"All-Cap Value with Growth Twist",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL015.X",market:"DE",sector:"Materials",strategy:"All-Cap Value with Growth Twist",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL016.X",market:"FR",sector:"Energy",strategy:"All-Cap Value with Growth Twist",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL017.X",market:"JP",sector:"Consumer Staples",strategy:"All-Cap Value with Growth Twist",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL018.X",market:"CA",sector:"Communication Services",strategy:"All-Cap Value with Growth Twist",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL019.X",market:"AU",sector:"Industrials",strategy:"All-Cap Value with Growth Twist",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL020.X",market:"SE",sector:"Information Technology",strategy:"All-Cap Value with Growth Twist",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL021.X",market:"CH",sector:"Financials",strategy:"Big Safe Dividends",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL022.X",market:"NL",sector:"Consumer Discretionary",strategy:"Big Safe Dividends",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL023.X",market:"BR",sector:"Health Care",strategy:"Big Safe Dividends",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL024.X",market:"KR",sector:"Materials",strategy:"Big Safe Dividends",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL025.X",market:"US",sector:"Energy",strategy:"Big Safe Dividends",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL026.X",market:"GB",sector:"Consumer Staples",strategy:"Big Safe Dividends",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL027.X",market:"DE",sector:"Communication Services",strategy:"Buy Good Do Nothing",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL028.X",market:"FR",sector:"Industrials",strategy:"Buy Good Do Nothing",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL029.X",market:"JP",sector:"Information Technology",strategy:"Buy Good Do Nothing",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL030.X",market:"CA",sector:"Financials",strategy:"Buy Good Do Nothing",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL031.X",market:"AU",sector:"Consumer Discretionary",strategy:"Buy Good Do Nothing",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL032.X",market:"SE",sector:"Health Care",strategy:"Buy Good Do Nothing",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL033.X",market:"CH",sector:"Materials",strategy:"Buy Good Do Nothing",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL034.X",market:"NL",sector:"Energy",strategy:"Buy Good Do Nothing",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL035.X",market:"BR",sector:"Consumer Staples",strategy:"Capital Compounders",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL036.X",market:"KR",sector:"Communication Services",strategy:"Capital Compounders",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL037.X",market:"US",sector:"Industrials",strategy:"Capital Compounders",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL038.X",market:"GB",sector:"Information Technology",strategy:"Capital Compounders",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL039.X",market:"DE",sector:"Financials",strategy:"Capital Compounders",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL040.X",market:"FR",sector:"Consumer Discretionary",strategy:"Capital Compounders",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL041.X",market:"JP",sector:"Health Care",strategy:"Capital Compounders",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL042.X",market:"CA",sector:"Materials",strategy:"Capital Compounders",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL043.X",market:"AU",sector:"Energy",strategy:"Contrarian Value",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL044.X",market:"SE",sector:"Consumer Staples",strategy:"Contrarian Value",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL045.X",market:"CH",sector:"Communication Services",strategy:"Contrarian Value",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL046.X",market:"NL",sector:"Industrials",strategy:"Contrarian Value",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL047.X",market:"BR",sector:"Information Technology",strategy:"Contrarian Value",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL048.X",market:"KR",sector:"Financials",strategy:"Deep Value",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL049.X",market:"US",sector:"Consumer Discretionary",strategy:"Deep Value",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL050.X",market:"GB",sector:"Health Care",strategy:"Deep Value",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL051.X",market:"DE",sector:"Materials",strategy:"Deep Value",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL052.X",market:"FR",sector:"Energy",strategy:"Durable Moat",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL053.X",market:"JP",sector:"Consumer Staples",strategy:"Durable Moat",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL054.X",market:"CA",sector:"Communication Services",strategy:"Durable Moat",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL055.X",market:"AU",sector:"Industrials",strategy:"Durable Moat",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL056.X",market:"SE",sector:"Information Technology",strategy:"Durable Moat",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL057.X",market:"CH",sector:"Financials",strategy:"Durable Moat",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL058.X",market:"NL",sector:"Consumer Discretionary",strategy:"Durable Moat",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL059.X",market:"BR",sector:"Health Care",strategy:"Global Bargains Hunting",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL060.X",market:"KR",sector:"Materials",strategy:"Global Bargains Hunting",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL061.X",market:"US",sector:"Energy",strategy:"Global Bargains Hunting",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL062.X",market:"GB",sector:"Consumer Staples",strategy:"Global Bargains Hunting",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL063.X",market:"DE",sector:"Communication Services",strategy:"Global Bargains Hunting",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL064.X",market:"FR",sector:"Industrials",strategy:"Global Bargains Hunting",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL065.X",market:"JP",sector:"Information Technology",strategy:"Global Bargains Hunting",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL066.X",market:"CA",sector:"Financials",strategy:"Global Bargains Hunting",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL067.X",market:"AU",sector:"Consumer Discretionary",strategy:"Low P/E Value Investing",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL068.X",market:"SE",sector:"Health Care",strategy:"Low P/E Value Investing",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL069.X",market:"CH",sector:"Materials",strategy:"Low P/E Value Investing",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL070.X",market:"NL",sector:"Energy",strategy:"Low P/E Value Investing",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL071.X",market:"BR",sector:"Consumer Staples",strategy:"Low P/E Value Investing",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL072.X",market:"KR",sector:"Communication Services",strategy:"Low P/E Value Investing",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL073.X",market:"US",sector:"Industrials",strategy:"Low P/E Value Investing",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL074.X",market:"GB",sector:"Information Technology",strategy:"Low P/E Value Investing",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL075.X",market:"DE",sector:"Financials",strategy:"Low P/E Value Investing",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL076.X",market:"FR",sector:"Consumer Discretionary",strategy:"Magic Formula",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL077.X",market:"JP",sector:"Health Care",strategy:"Magic Formula",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL078.X",market:"CA",sector:"Materials",strategy:"Magic Formula",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL079.X",market:"AU",sector:"Energy",strategy:"Magic Formula",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL080.X",market:"SE",sector:"Consumer Staples",strategy:"Magic Formula",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL081.X",market:"CH",sector:"Communication Services",strategy:"Magic Formula",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL082.X",market:"NL",sector:"Industrials",strategy:"Magic Formula",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL083.X",market:"BR",sector:"Information Technology",strategy:"Market Leaders Low P/CF",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL084.X",market:"KR",sector:"Financials",strategy:"Market Leaders Low P/CF",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL085.X",market:"US",sector:"Consumer Discretionary",strategy:"Market Leaders Low P/CF",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL086.X",market:"GB",sector:"Health Care",strategy:"Market Leaders Low P/CF",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL087.X",market:"DE",sector:"Materials",strategy:"Market Leaders Low P/CF",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL088.X",market:"FR",sector:"Energy",strategy:"Market Leaders Low P/CF",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL089.X",market:"JP",sector:"Consumer Staples",strategy:"Market Leaders Low P/CF",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL090.X",market:"CA",sector:"Communication Services",strategy:"Market Leaders Low P/CF",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL091.X",market:"AU",sector:"Industrials",strategy:"Modern Value Investing",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL092.X",market:"SE",sector:"Information Technology",strategy:"Modern Value Investing",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL093.X",market:"CH",sector:"Financials",strategy:"Modern Value Investing",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL094.X",market:"NL",sector:"Consumer Discretionary",strategy:"Modern Value Investing",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL095.X",market:"BR",sector:"Health Care",strategy:"Modern Value Investing",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL096.X",market:"KR",sector:"Materials",strategy:"Modern Value Investing",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL097.X",market:"US",sector:"Energy",strategy:"Modern Value Investing",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL098.X",market:"GB",sector:"Consumer Staples",strategy:"Modern Value Investing",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL099.X",market:"DE",sector:"Communication Services",strategy:"Modern Value Investing",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL100.X",market:"FR",sector:"Industrials",strategy:"One Up",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL101.X",market:"JP",sector:"Information Technology",strategy:"One Up",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL102.X",market:"CA",sector:"Financials",strategy:"One Up",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL103.X",market:"AU",sector:"Consumer Discretionary",strategy:"One Up",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL104.X",market:"SE",sector:"Health Care",strategy:"One Up",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL105.X",market:"CH",sector:"Materials",strategy:"One Up",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL106.X",market:"NL",sector:"Energy",strategy:"One Up",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL107.X",market:"BR",sector:"Consumer Staples",strategy:"Only the Best",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL108.X",market:"KR",sector:"Communication Services",strategy:"Only the Best",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL109.X",market:"US",sector:"Industrials",strategy:"Only the Best",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL110.X",market:"GB",sector:"Information Technology",strategy:"Only the Best",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL111.X",market:"DE",sector:"Financials",strategy:"Only the Best",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL112.X",market:"FR",sector:"Consumer Discretionary",strategy:"Only the Best",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL113.X",market:"JP",sector:"Health Care",strategy:"Only the Best",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL114.X",market:"CA",sector:"Materials",strategy:"Only the Best",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL115.X",market:"AU",sector:"Energy",strategy:"Only the Best",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL116.X",market:"SE",sector:"Consumer Staples",strategy:"Owning the Best",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL117.X",market:"CH",sector:"Communication Services",strategy:"Owning the Best",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL118.X",market:"NL",sector:"Industrials",strategy:"Owning the Best",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL119.X",market:"BR",sector:"Information Technology",strategy:"Owning the Best",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL120.X",market:"KR",sector:"Financials",strategy:"Owning the Best",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL121.X",market:"US",sector:"Consumer Discretionary",strategy:"Owning the Best",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL122.X",market:"GB",sector:"Health Care",strategy:"Owning the Best",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL123.X",market:"DE",sector:"Materials",strategy:"Owning the Best",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL124.X",market:"FR",sector:"Energy",strategy:"Owning the Best",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL125.X",market:"JP",sector:"Consumer Staples",strategy:"Permanent Portfolio",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL126.X",market:"CA",sector:"Communication Services",strategy:"Permanent Portfolio",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL127.X",market:"AU",sector:"Industrials",strategy:"Permanent Portfolio",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL128.X",market:"SE",sector:"Information Technology",strategy:"Permanent Portfolio",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL129.X",market:"CH",sector:"Financials",strategy:"Permanent Portfolio",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL130.X",market:"NL",sector:"Consumer Discretionary",strategy:"Permanent Portfolio",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL131.X",market:"BR",sector:"Health Care",strategy:"Permanent Portfolio",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL132.X",market:"KR",sector:"Materials",strategy:"Permanent Portfolio",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL133.X",market:"US",sector:"Energy",strategy:"Permanent Portfolio",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL134.X",market:"GB",sector:"Consumer Staples",strategy:"Permanent Portfolio",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL135.X",market:"DE",sector:"Communication Services",strategy:"Piotroski F-Score",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL136.X",market:"FR",sector:"Industrials",strategy:"Piotroski F-Score",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL137.X",market:"JP",sector:"Information Technology",strategy:"Piotroski F-Score",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL138.X",market:"CA",sector:"Financials",strategy:"Piotroski F-Score",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL139.X",market:"AU",sector:"Consumer Discretionary",strategy:"Piotroski F-Score",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL140.X",market:"SE",sector:"Health Care",strategy:"Piotroski F-Score",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL141.X",market:"CH",sector:"Materials",strategy:"Piotroski F-Score",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL142.X",market:"NL",sector:"Energy",strategy:"Piotroski F-Score",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL143.X",market:"BR",sector:"Consumer Staples",strategy:"Quality Growth Investing",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL144.X",market:"KR",sector:"Communication Services",strategy:"Quality Growth Investing",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL145.X",market:"US",sector:"Industrials",strategy:"Quality Growth Investing",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL146.X",market:"GB",sector:"Information Technology",strategy:"Quality Growth Investing",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL147.X",market:"DE",sector:"Financials",strategy:"Quality Growth Investing",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL148.X",market:"FR",sector:"Consumer Discretionary",strategy:"Quality Growth Investing",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL149.X",market:"JP",sector:"Health Care",strategy:"Quality Growth Investing",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL150.X",market:"CA",sector:"Materials",strategy:"Quality Growth Investing",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL151.X",market:"AU",sector:"Energy",strategy:"Quality Growth Investing",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL152.X",market:"SE",sector:"Consumer Staples",strategy:"Rule 1",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL153.X",market:"CH",sector:"Communication Services",strategy:"Rule 1",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL154.X",market:"NL",sector:"Industrials",strategy:"Rule 1",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL155.X",market:"BR",sector:"Information Technology",strategy:"Rule 1",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL156.X",market:"KR",sector:"Financials",strategy:"Rule 1",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL157.X",market:"US",sector:"Consumer Discretionary",strategy:"Rule 1",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL158.X",market:"GB",sector:"Health Care",strategy:"Rule 1",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL159.X",market:"DE",sector:"Materials",strategy:"Rule 1",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL160.X",market:"FR",sector:"Energy",strategy:"Rule 1",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL161.X",market:"JP",sector:"Consumer Staples",strategy:"Small Cap Value & Growth",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL162.X",market:"CA",sector:"Communication Services",strategy:"Small Cap Value & Growth",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL163.X",market:"AU",sector:"Industrials",strategy:"Small Cap Value & Growth",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL164.X",market:"SE",sector:"Information Technology",strategy:"Small Cap Value & Growth",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL165.X",market:"CH",sector:"Financials",strategy:"Small Cap Value & Growth",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL166.X",market:"NL",sector:"Consumer Discretionary",strategy:"Small Cap Value & Growth",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL167.X",market:"BR",sector:"Health Care",strategy:"Small Cap Value & Growth",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL168.X",market:"KR",sector:"Materials",strategy:"Small Cap Value & Growth",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL169.X",market:"US",sector:"Energy",strategy:"Small Cap Value & Growth",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL170.X",market:"GB",sector:"Consumer Staples",strategy:"The Intelligent Investor - Defensive",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL171.X",market:"DE",sector:"Communication Services",strategy:"The Intelligent Investor - Defensive",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL172.X",market:"FR",sector:"Industrials",strategy:"The Intelligent Investor - Defensive",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL173.X",market:"JP",sector:"Information Technology",strategy:"The Intelligent Investor - Enterprising",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL174.X",market:"CA",sector:"Financials",strategy:"The Intelligent Investor - Enterprising",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL175.X",market:"AU",sector:"Consumer Discretionary",strategy:"The Intelligent Investor - Enterprising",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL176.X",market:"SE",sector:"Health Care",strategy:"The Intelligent Investor - Enterprising",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL177.X",market:"CH",sector:"Materials",strategy:"The Intelligent Investor - Enterprising",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL178.X",market:"NL",sector:"Energy",strategy:"The Intelligent Investor - Enterprising",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL179.X",market:"BR",sector:"Consumer Staples",strategy:"The Intelligent Investor - Enterprising",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL180.X",market:"KR",sector:"Communication Services",strategy:"The Intelligent Investor - Enterprising",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL181.X",market:"US",sector:"Industrials",strategy:"The Moat",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL182.X",market:"GB",sector:"Information Technology",strategy:"The Moat",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL183.X",market:"DE",sector:"Financials",strategy:"The Moat",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL184.X",market:"FR",sector:"Consumer Discretionary",strategy:"The Moat",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL185.X",market:"JP",sector:"Health Care",strategy:"The Moat",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL186.X",market:"CA",sector:"Materials",strategy:"The Moat",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL187.X",market:"AU",sector:"Energy",strategy:"The Moat",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL188.X",market:"SE",sector:"Consumer Staples",strategy:"The Moat",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL189.X",market:"CH",sector:"Communication Services",strategy:"The Moat",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL190.X",market:"NL",sector:"Industrials",strategy:"The Zulu Principle",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL191.X",market:"BR",sector:"Information Technology",strategy:"The Zulu Principle",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL192.X",market:"KR",sector:"Financials",strategy:"The Zulu Principle",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL193.X",market:"US",sector:"Consumer Discretionary",strategy:"The Zulu Principle",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL194.X",market:"GB",sector:"Health Care",strategy:"The Zulu Principle",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL195.X",market:"DE",sector:"Materials",strategy:"The Zulu Principle",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL196.X",market:"FR",sector:"Energy",strategy:"The Zulu Principle",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL197.X",market:"JP",sector:"Consumer Staples",strategy:"The Zulu Principle",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL198.X",market:"CA",sector:"Communication Services",strategy:"The Zulu Principle",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL199.X",market:"AU",sector:"Industrials",strategy:"The Zulu Principle",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL200.X",market:"SE",sector:"Information Technology",strategy:"Tiny Titans",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL201.X",market:"CH",sector:"Financials",strategy:"Tiny Titans",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL202.X",market:"NL",sector:"Consumer Discretionary",strategy:"Tiny Titans",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL203.X",market:"BR",sector:"Health Care",strategy:"Tiny Titans",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL204.X",market:"KR",sector:"Materials",strategy:"Tiny Titans",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL205.X",market:"US",sector:"Energy",strategy:"Tiny Titans",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL206.X",market:"GB",sector:"Consumer Staples",strategy:"Tiny Titans",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL207.X",market:"DE",sector:"Communication Services",strategy:"Tiny Titans",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL208.X",market:"FR",sector:"Industrials",strategy:"Tiny Titans",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL209.X",market:"JP",sector:"Information Technology",strategy:"Tiny Titans",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL210.X",market:"CA",sector:"Financials",strategy:"Tiny Titans",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL211.X",market:"AU",sector:"Consumer Discretionary",strategy:"Value Investing Toolbox - Core",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL212.X",market:"SE",sector:"Health Care",strategy:"Value Investing Toolbox - Core",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL213.X",market:"CH",sector:"Materials",strategy:"Value Investing Toolbox - Core",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL214.X",market:"NL",sector:"Energy",strategy:"Value Investing Toolbox - Core",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL215.X",market:"BR",sector:"Consumer Staples",strategy:"Value Investing Toolbox - Core",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL216.X",market:"KR",sector:"Communication Services",strategy:"Value Investing Toolbox - Core",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL217.X",market:"US",sector:"Industrials",strategy:"Value Investing Toolbox - Core",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL218.X",market:"GB",sector:"Information Technology",strategy:"Value Investing Toolbox - Core",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL219.X",market:"DE",sector:"Financials",strategy:"Value Investing Toolbox - Core",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL220.X",market:"FR",sector:"Consumer Discretionary",strategy:"Value Investing Toolbox - Value",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL221.X",market:"JP",sector:"Health Care",strategy:"Value Investing Toolbox - Value",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL222.X",market:"CA",sector:"Materials",strategy:"Value Investing Toolbox - Value",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL223.X",market:"AU",sector:"Energy",strategy:"Value Investing Toolbox - Value",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL224.X",market:"SE",sector:"Consumer Staples",strategy:"Value Investing Toolbox - Value",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL225.X",market:"CH",sector:"Communication Services",strategy:"Value Investing Toolbox - Value",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL226.X",market:"NL",sector:"Industrials",strategy:"Value Investing Toolbox - Value",stock_style:"Income",cap:"Large Cap"},
  {symbol:"FILL227.X",market:"BR",sector:"Information Technology",strategy:"Value Investing Toolbox - Value",stock_style:"Core",cap:"Mid Cap"},
  {symbol:"FILL228.X",market:"KR",sector:"Financials",strategy:"Value Investing Toolbox - Value",stock_style:"GARP",cap:"Small Cap"},
  {symbol:"FILL229.X",market:"US",sector:"Consumer Discretionary",strategy:"Zebra in Lion Country",stock_style:"Value",cap:"Mega Cap"},
  {symbol:"FILL230.X",market:"GB",sector:"Health Care",strategy:"Zebra in Lion Country",stock_style:"Quality",cap:"Large Cap"},
  {symbol:"FILL231.X",market:"DE",sector:"Materials",strategy:"Zebra in Lion Country",stock_style:"Growth",cap:"Mid Cap"},
  {symbol:"FILL232.X",market:"FR",sector:"Energy",strategy:"Zebra in Lion Country",stock_style:"Income",cap:"Small Cap"},
  {symbol:"FILL233.X",market:"JP",sector:"Consumer Staples",strategy:"Zebra in Lion Country",stock_style:"Core",cap:"Mega Cap"},
  {symbol:"FILL234.X",market:"CA",sector:"Communication Services",strategy:"Zebra in Lion Country",stock_style:"GARP",cap:"Large Cap"},
  {symbol:"FILL235.X",market:"AU",sector:"Industrials",strategy:"Zebra in Lion Country",stock_style:"Value",cap:"Mid Cap"},
  {symbol:"FILL236.X",market:"SE",sector:"Information Technology",strategy:"Zebra in Lion Country",stock_style:"Quality",cap:"Small Cap"},
  {symbol:"FILL237.X",market:"CH",sector:"Financials",strategy:"Zebra in Lion Country",stock_style:"Growth",cap:"Mega Cap"},
  {symbol:"FILL238.X",market:"NL",sector:"Consumer Discretionary",strategy:"Zebra in Lion Country",stock_style:"Income",cap:"Large Cap"},
].map(parseComp);

/* ─── ATOMS ──────────────────────────────────────────────────── */
const card = { background:"var(--paper)", borderRadius:16, border:"1px solid var(--line)", boxShadow:"0 2px 12px rgba(16,36,24,.06)" };
const cardPad = { padding:"14px 16px" };

function Bdg({ tone="n", children }) {
  const map = {
    g:{ bg:"rgba(111,138,91,.12)", col:C.green,  bd:"rgba(111,138,91,.28)" },
    r:{ bg:"rgba(198,82,63,.12)",  col:C.red,    bd:"rgba(198,82,63,.28)" },
    w:{ bg:"rgba(202,168,94,.14)", col:C.amber,  bd:"rgba(202,168,94,.32)" },
    n:{ bg:"rgba(181,154,91,.12)", col:C.gold,   bd:"rgba(181,154,91,.28)" },
  };
  const t = map[tone]||map.n;
  return <span style={{ background:t.bg, color:t.col, border:`1px solid ${t.bd}`, padding:"3px 10px", borderRadius:999, fontSize:10, fontWeight:800, letterSpacing:.04, display:"inline-flex", alignItems:"center" }}>{children}</span>;
}

function MK({ label, value, color }) {
  return (
    <div style={{ background:"var(--paper-alt)", border:"1px solid var(--line)", borderRadius:10, padding:"8px 10px" }}>
      <div style={{ fontSize:9, fontWeight:800, textTransform:"uppercase", letterSpacing:".12em", color:"var(--muted)" }}>{label}</div>
      <div style={{ ...MONO, fontSize:14, fontWeight:800, color, lineHeight:1, marginTop:3 }}>{value}</div>
    </div>
  );
}

function Dsec({ children }) {
  return <div style={{ fontSize:9, fontWeight:800, textTransform:"uppercase", letterSpacing:".12em", color:"var(--muted)", marginBottom:8 }}>{children}</div>;
}

function Avatar({ name, size=18 }) {
  const { investors } = useStrategiesData();
  const inv = investors[name]; const col = avC(name);
  const ini = inv?.ini?.slice(0,2) || name.split(" ").map(w=>w[0]).slice(0,2).join("");
  return <div style={{ width:size, height:size, borderRadius:"50%", flexShrink:0, background:hA(col,.2), color:col, border:`1.5px solid ${hA(col,.45)}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:size*.38, fontWeight:900, ...MONO, lineHeight:1 }}>{ini}</div>;
}

function StylePill({ s, title }) {
  const sc = stT(s);
  return (
    <span
      title={title || undefined}
      style={{ fontSize:9, fontWeight:800, padding:"1px 5px", borderRadius:4, background:sc.bg, border:`1px solid ${sc.bd}`, color:sc.col }}
    >
      {s}
    </span>
  );
}

function InvChip({ name, onNav }) {
  const { investors } = useStrategiesData();
  const inv = investors[name];
  if (!name || !inv) return <span style={{ fontSize:11, color:"var(--muted)" }}>—</span>;
  return (
    <span className="inv-chip" onClick={e => { e.stopPropagation(); onNav(name); }}>
      <Avatar name={name} size={18}/>
      <span className="inv-chip-name" style={{ fontSize:11, fontWeight:700, color:"var(--ink)" }}>{name}</span>
      <StylePill s={inv.style}/>
    </span>
  );
}

function Sparkbar({ monthly }) {
  const vals = monthly.map(d=>d.val).filter(v=>v!=null);
  const mx = Math.max(...vals.map(Math.abs),1);
  return (
    <div className="isp-spark">
      {monthly.map((d,i) => <span key={i} style={{ height:d.val==null?2:Math.max(2,Math.abs(d.val)/mx*20), background:d.val==null?"var(--line)":d.val>=0?C.green:C.red }} title={d.val!=null?`${d.m}: ${fP(d.val)}`:`${d.m}: —`}/>)}
    </div>
  );
}

/* ─── SVG CHARTS ─────────────────────────────────────────────── */
function DonutSVG({ items, total, size=64 }) {
  let paths=[]; let ang=-Math.PI/2; const r=size/2-4,cx=size/2,cy=size/2;
  items.forEach(([label,cnt,col],i) => {
    if(!cnt) return;
    const theta=(cnt/total)*2*Math.PI;
    const x1=cx+r*Math.cos(ang),y1=cy+r*Math.sin(ang); ang+=theta;
    const x2=cx+r*Math.cos(ang),y2=cy+r*Math.sin(ang);
    paths.push(<path key={i} d={`M${cx},${cy} L${x1.toFixed(1)},${y1.toFixed(1)} A${r},${r} 0 ${theta>Math.PI?1:0},1 ${x2.toFixed(1)},${y2.toFixed(1)} Z`} fill={col} opacity={.88} stroke="var(--paper)" strokeWidth={1}><title>{label}: {cnt}</title></path>);
  });
  return (
    <svg width={size} height={size} style={{ flexShrink:0 }}>
      {paths}
      <circle cx={cx} cy={cy} r={Math.round(r*.46)} fill="var(--paper-alt)"/>
      <text x={cx} y={cy+4} textAnchor="middle" fill="var(--ink)" style={{ fontSize:11,...MONO,fontWeight:700 }}>{total}</text>
    </svg>
  );
}

function LineSVG({ monthly }) {
  const W=480,H=52;
  const vals=monthly.map(d=>d.val??0); let cum=0;
  const pts=vals.map(v=>{cum+=v;return cum;});
  const mn=Math.min(...pts,0),mx=Math.max(...pts,0.01),rng=mx-mn||1;
  const px=(v,i)=>({x:10+(i/(pts.length-1||1))*(W-20),y:H-8-((v-mn)/rng)*(H-16)});
  const poly=pts.map((v,i)=>`${px(v,i).x.toFixed(1)},${px(v,i).y.toFixed(1)}`).join(" ");
  const col=pts[pts.length-1]>=0?C.green:C.red;
  const zy=(H-8-((0-mn)/rng)*(H-16)).toFixed(1);
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow:"visible" }}>
      <line x1="10" y1={zy} x2={W-10} y2={zy} stroke="var(--line)" strokeWidth={1} strokeDasharray="3,2"/>
      <polyline points={poly} fill="none" stroke={col} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"/>
      {[0,pts.length-1].map(i=>{const p=px(pts[i],i);return <circle key={i} cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r={3} fill={pts[i]>=0?C.green:C.red}/>;})}
      {MONTHS.map((m,i)=>{const x=10+(i/(MONTHS.length-1))*(W-20);return <text key={i} x={x.toFixed(1)} y={H} textAnchor="middle" style={{fontSize:7,fill:"var(--muted)",...MONO}}>{m.slice(0,1)}</text>;})}
    </svg>
  );
}

function CagrSVG({ s }) {
  const W=480,H=44; const data=[["2Y",s.c2],["3Y",s.c3],["4Y",s.c4],["5Y",s.c5]].filter(([,v])=>v!=null);
  if(!data.length) return <span style={{color:"var(--muted)",fontSize:11}}>—</span>;
  const mx=Math.max(...data.map(([,v])=>Math.abs(v)),1); const bw=Math.floor((W-20)/data.length); const zy=H-14;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H+6}`} style={{overflow:"visible"}}>
      <line x1="0" y1={zy} x2={W} y2={zy} stroke="var(--line)" strokeWidth={1}/>
      {data.map(([l,v],i)=>{
        const x=10+i*bw+bw*.1,bww=bw*.8,bh=Math.max(2,Math.abs(v)/mx*(zy-8)),y=v>=0?zy-bh:zy,col=v>=0?C.green:C.red;
        return <g key={l}>
          <rect x={x.toFixed(0)} y={y.toFixed(0)} width={bww.toFixed(0)} height={bh.toFixed(0)} fill={col} opacity={.85} rx={2}/>
          <text x={(x+bww/2).toFixed(0)} y={(v>=0?y-3:y+bh+10).toFixed(0)} textAnchor="middle" style={{fontSize:8,fill:col,...MONO,fontWeight:700}}>{fP(v,0)}</text>
          <text x={(x+bww/2).toFixed(0)} y={(H+4).toFixed(0)} textAnchor="middle" style={{fontSize:8,fill:"var(--muted)",...MONO}}>{l}</text>
        </g>;
      })}
    </svg>
  );
}

/* ─── SIGNAL ─────────────────────────────────────────────────── */
function computeSignal(c) {
  if (c._sig) return c._sig === "BUY" ? "BUY" : c._sig === "HOLD" ? "HOLD" : "WATCH";
  if (c._is == null) return "WATCH";
  const mosNorm = Math.min(100, Math.max(0, ((Number(c._mo) || 0) + 50) / 1.5));
  const score =
    c._is * 0.4 +
    (Number(c._fn) || 0) * 0.3 +
    (Number(c._ti) || 0) * 0.2 +
    mosNorm * 0.1;
  if (score >= 72) return "BUY";
  if (score >= 58) return "HOLD";
  return "WATCH";
}
const SIG_STYLE = {
  "BUY":  { bg:"rgba(111,138,91,.15)", bd:"rgba(111,138,91,.4)",  col:"#6f8a5b" },
  "HOLD": { bg:"rgba(202,168,94,.14)", bd:"rgba(202,168,94,.4)",  col:"#caa85e" },
  "WATCH":{ bg:"rgba(198,82,63,.12)",  bd:"rgba(198,82,63,.35)",  col:"#c6523f" },
};

/* ─── CONSTITUENTS TABLE ─────────────────────────────────────── */
const CT_PG=25; const CAP_ORD=["Mega Cap","Large Cap","Mid Cap","Small Cap","Nano Cap"];
const SEC_SHORT = s=>s.replace("Information Technology","IT").replace("Consumer Discretionary","Cons. Disc.").replace("Consumer Staples","Cons. Staples").replace("Communication Services","Comm. Serv.").replace("Real Estate","Real Est.").replace("Health Care","Health");

/* ─── CT TABLE HEADER CELL (module-level — never inside CTTable) ── */
const REG_ORD = [
  "Amérique du Nord",
  "Amérique du Sud",
  "Amérique latine",
  "Europe",
  "Asie du Nord",
  "Asie du Sud",
  "Asie du Sud-Est",
  "Asie",
  "Moyen-Orient",
  "Afrique",
  "Océanie",
  "Asie centrale",
  "Europe de l'Est",
];

function CTTable({ comp }) {
  const { onOpenTicker } = useStrategiesData();
  const [pg,setPg]=useState(0);
  const [sk,setSk]=useState("_is");
  const [asc,setAsc]=useState(false);
  const [isMob,setIsMob]=useState(()=>typeof window!=="undefined"&&window.innerWidth<680);
  useEffect(()=>{
    const fn=()=>setIsMob(window.innerWidth<680);
    window.addEventListener("resize",fn);
    return()=>window.removeEventListener("resize",fn);
  },[]);

  const sorted=useMemo(()=>{
    const numDesc = (get) => (a, b) => {
      const na = Number(get(a));
      const nb = Number(get(b));
      const va = Number.isFinite(na) ? na : -1e9;
      const vb = Number.isFinite(nb) ? nb : -1e9;
      return vb - va;
    };
    const fns={
      _is:numDesc((x)=>x._is), _fn:numDesc((x)=>x._fn),
      _mo:numDesc((x)=>x._mo), _ti:numDesc((x)=>x._ti),
      sig:(a,b)=>["BUY","HOLD","WATCH"].indexOf(computeSignal(a))-["BUY","HOLD","WATCH"].indexOf(computeSignal(b)),
      sym:(a,b)=>a.symbol.localeCompare(b.symbol),
      region:(a,b)=>REG_ORD.indexOf(getRegion(a))-REG_ORD.indexOf(getRegion(b)),
      market:(a,b)=>a.market.localeCompare(b.market),
      sector:(a,b)=>a.sector.localeCompare(b.sector),
      cap:(a,b)=>CAP_ORD.indexOf(a.cap)-CAP_ORD.indexOf(b.cap),
      style:(a,b)=>a.stock_style.localeCompare(b.stock_style),
    };
    const r=[...comp].sort(fns[sk]||fns._is);
    return asc?r.reverse():r;
  },[comp,sk,asc]);

  const total=sorted.length;
  const pages=Math.ceil(total/CT_PG);
  const slice=sorted.slice(pg*CT_PG,(pg+1)*CT_PG);
  function hs(k){ if(sk===k)setAsc(a=>!a); else{setSk(k);setAsc(false);setPg(0);} }
  function thS(k,left){ return {padding:"7px 8px",fontSize:8,...MONO,letterSpacing:".09em",textTransform:"uppercase",color:sk===k?C.gold:"var(--muted)",borderBottom:"2px solid rgba(30,40,34,.10)",whiteSpace:"nowrap",cursor:"pointer",userSelect:"none",textAlign:left?"left":"right",background:"#f7f5f0"}; }
  const TD={padding:"9px 8px",borderBottom:"1px solid rgba(30,40,34,.06)",verticalAlign:"middle",whiteSpace:"nowrap"};

  /* ── Pagination bar (shared desktop+mobile) ── */
  const Pager = ()=>(
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,flexWrap:"wrap",gap:6}}>
        <Dsec>Constituants · {total} titre{total>1?"s":""}</Dsec>
        <div style={{display:"flex",gap:4,alignItems:"center"}}>
        <span style={{fontSize:10,color:"var(--muted)",...MONO}}>{pg*CT_PG+1}–{Math.min((pg+1)*CT_PG,total)} / {total}</span>
          <button onClick={()=>setPg(p=>Math.max(0,p-1))} disabled={pg===0}
          style={{width:28,height:28,borderRadius:7,border:"1px solid rgba(30,40,34,.10)",background:"#f7f5f0",color:pg===0?"var(--muted)":C.gold,fontSize:15,cursor:pg===0?"default":"pointer",lineHeight:1,transition:"all .15s"}}>‹</button>
          <button onClick={()=>setPg(p=>Math.min(pages-1,p+1))} disabled={pg>=pages-1}
          style={{width:28,height:28,borderRadius:7,border:"1px solid rgba(30,40,34,.10)",background:"#f7f5f0",color:pg>=pages-1?"var(--muted)":C.gold,fontSize:15,cursor:pg>=pages-1?"default":"pointer",lineHeight:1,transition:"all .15s"}}>›</button>
        </div>
      </div>
  );

  /* ────────────────────────────────────────────────────────────
     MOBILE — card layout
  ──────────────────────────────────────────────────────────── */
  if(isMob) return (
    <div>
      <Pager/>
      {/* Mobile sort bar */}
      <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:10,alignItems:"center"}}>
        <span style={{fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".10em",color:"var(--muted)",...MONO}}>Trier :</span>
        {[["IS","_is"],["Fund","_fn"],["Signal","sig"],["MoS","_mo"],["Trend","_ti"]].map(([l,k])=>(
          <button key={k} onClick={()=>hs(k)}
            style={{padding:"4px 10px",borderRadius:999,fontSize:10,fontWeight:800,cursor:"pointer",
              border:`1px solid ${sk===k?"rgba(181,154,91,.5)":"rgba(30,40,34,.10)"}`,
              background:sk===k?"rgba(181,154,91,.12)":"#fff",
              color:sk===k?C.gold:"var(--muted)",...MONO,transition:"all .15s"}}>
            {l}{sk===k?(asc?" ▲":" ▼"):""}
          </button>
        ))}
      </div>
      {/* Cards */}
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {slice.map((c,i)=>{
          const sig=computeSignal(c); const ss=SIG_STYLE[sig];
          const mv=fmtMoVal(c);
          const region=getRegion(c);
          const stSc=stT(c.stock_style);
          const capShort=c.cap.replace(" Cap","");
          const capCol=CAP_C[c.cap]||C.gold;
          return (
            <div key={c.symbol+i} style={{background:"#fff",border:"1px solid rgba(30,40,34,.08)",borderRadius:12,
              padding:"11px 13px",boxShadow:"0 1px 4px rgba(16,24,20,.05)",borderLeft:`3px solid ${ss.col}`}}>
              {/* Row 1: symbol + signal + MoS */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
                  <TickerSymbolLink symbol={c.symbol} onOpen={onOpenTicker} style={{ fontSize: 13 }} truncate={0} />
                  <span style={{fontSize:8,fontWeight:800,padding:"2px 6px",borderRadius:4,
                    background:stSc.bg,border:`1px solid ${stSc.bd}`,color:stSc.col,whiteSpace:"nowrap"}}>
                    {c.stock_style}
                  </span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:7,flexShrink:0}}>
                  <span style={{fontSize:11,fontWeight:900,...MONO,color:mosC(moNum(c))}}>{mv}</span>
                  <Tip text={`Signal composite :\nBUY ≥72 · HOLD ≥58 · WATCH <58\nIS×40% + Fund×30% + Trend×20% + MoS×10%`}>
                    <span style={{fontSize:9,fontWeight:900,padding:"2px 8px",borderRadius:999,
                      background:ss.bg,border:`1px solid ${ss.bd}`,color:ss.col,...MONO}}>{sig}</span>
                  </Tip>
                </div>
              </div>
              {/* Row 2: region dot + region + market · sector · cap */}
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                <div style={{display:"flex",alignItems:"center",gap:4}}>
                  <div style={{width:7,height:7,borderRadius:2,background:REGION_COLOR_MAP[region]||C.gold,flexShrink:0}}/>
                  <span style={{fontSize:10,color:"var(--muted)",fontWeight:600}}>{region}</span>
                </div>
                <span style={{fontSize:10,color:"rgba(30,40,34,.25)"}}>·</span>
                <span style={{...MONO,fontSize:10,fontWeight:700,color:"#4a5a52"}}>{c.market}</span>
                <span style={{fontSize:10,color:"rgba(30,40,34,.25)"}}>·</span>
                <div style={{display:"flex",alignItems:"center",gap:4}}>
                  <div style={{width:6,height:6,borderRadius:1,background:SECTOR_C[c.sector]||C.gold,flexShrink:0}}/>
                  <span style={{fontSize:10,color:"var(--muted)"}}>{SEC_SHORT(c.sector)}</span>
                </div>
                <span style={{fontSize:10,color:"rgba(30,40,34,.25)"}}>·</span>
                <span style={{fontSize:10,fontWeight:800,...MONO,color:capCol}}>{capShort}</span>
              </div>
              {/* Row 3: IS · Fund · Trend scores avec mini-barres */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:6}}>
                {[["IS",c._is,sC(c._is),"Flux de capitaux nets\n(Inflow Score) 0-100"],["Fund",c._fn,sC(c._fn),"Score Fondamental\nQualité financière 0-100"],["Trend",c._ti,sC(c._ti),"Score de Tendance\nMomentum du cours 0-100"]].map(([l,v,col,tipTxt])=>(
                  <div key={l} style={{background:"#f7f5f0",borderRadius:7,padding:"5px 7px",border:"1px solid rgba(30,40,34,.06)"}}>
                    <Tip text={tipTxt}><div style={{fontSize:8,color:"var(--muted)",...MONO,textTransform:"uppercase",letterSpacing:".08em",marginBottom:3}}>{l}</div></Tip>
                    <div style={{...MONO,fontSize:13,fontWeight:800,color:col,lineHeight:1}}>{fmtScore(v, l === "Trend" ? 0 : 1)}</div>
                    <div style={{height:3,borderRadius:999,background:"rgba(30,40,34,.07)",marginTop:4,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${Number.isFinite(Number(v)) ? Math.min(100, Math.max(0, Number(v))) : 0}%`,background:col,borderRadius:999}}/>
                    </div>
                  </div>
                ))}
                <div style={{background:"#f7f5f0",borderRadius:7,padding:"5px 7px",border:"1px solid rgba(30,40,34,.06)"}}>
                  <Tip text={"Marge de Sécurité\nDécote vs valeur intrinsèque.\n+20% = très sous-évalué"}><div style={{fontSize:8,color:"var(--muted)",...MONO,textTransform:"uppercase",letterSpacing:".08em",marginBottom:3}}>MoS</div></Tip>
                  <div style={{...MONO,fontSize:13,fontWeight:800,color:mosC(moNum(c)),lineHeight:1}}>{mv}</div>
                  <div style={{height:3,borderRadius:999,background:"rgba(30,40,34,.07)",marginTop:4,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${Math.min(100,Math.max(0,moNum(c)))}%`,background:mosC(moNum(c)),borderRadius:999}}/>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  /* ────────────────────────────────────────────────────────────
     DESKTOP — table layout
  ──────────────────────────────────────────────────────────── */
  return (
    <div>
      <Pager/>
      <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch",borderRadius:12,border:"1px solid rgba(30,40,34,.08)"}}>
        <table className="isp-ct" style={{width:"100%",borderCollapse:"collapse",minWidth:960}}>
          <thead>
            <tr>
              <th style={thS("sym",true)}    onClick={()=>hs("sym")}><Tip text={`Symbole boursier du titre\n(ex: AAPL = Apple)`}>Valeur</Tip><SortIcon active={sk==="sym"} asc={asc} /></th>
              <th style={thS("region",true)} onClick={()=>hs("region")}><Tip text={`Zone géographique du marché\ncotant le titre`}>Région</Tip><SortIcon active={sk==="region"} asc={asc} /></th>
              <th style={thS("market",true)} onClick={()=>hs("market")}><Tip text={`Code du marché boursier\n(ex: US, FR, JP, GB)`}>Mkt</Tip><SortIcon active={sk==="market"} asc={asc} /></th>
              <th style={thS("sector",true)} onClick={()=>hs("sector")}><Tip text={`Secteur d'activité de l'entreprise\nselon la classification GICS`}>Secteur</Tip><SortIcon active={sk==="sector"} asc={asc} /></th>
              <th style={thS("cap",false)}   onClick={()=>hs("cap")}><Tip text={`Capitalisation boursière :\nMega (>200Mds) · Large (10-200Mds)\nMid (2-10Mds) · Small (<2Mds)`}>Cap</Tip><SortIcon active={sk==="cap"} asc={asc} /></th>
              <th style={thS("style",false)} onClick={()=>hs("style")}><Tip text={`Style de sélection du titre :\nValue (sous-évalué) · Growth (croissance)\nQuality (qualité) · Income (dividendes)`}>Style</Tip><SortIcon active={sk==="style"} asc={asc} /></th>
              <th style={thS("sig",false)}   onClick={()=>hs("sig")}><Tip text={`Signal composite : BUY / HOLD / WATCH\nCalculé : IS×40% + Fund×30% + Trend×20% + MoS×10%`}>Signal</Tip><SortIcon active={sk==="sig"} asc={asc} /></th>
              <th style={thS("_is",false)}   onClick={()=>hs("_is")}><Tip text={`IScore — Score de flux de capitaux nets\nMesure l'attractivité du titre\npour les investisseurs institutionnels (0-100)`}>IS</Tip><SortIcon active={sk==="_is"} asc={asc} /></th>
              <th style={thS("_fn",false)}   onClick={()=>hs("_fn")}><Tip text={`Score Fondamental — Qualité des\nfondamentaux financiers de l'entreprise\n(rentabilité, bilan, croissance) (0-100)`}>Fund</Tip><SortIcon active={sk==="_fn"} asc={asc} /></th>
              <th style={thS("_ti",false)}   onClick={()=>hs("_ti")}><Tip text={`Score de Tendance — Force de la\ntendance haussière du cours\n(momentum technique) (0-100)`}>Trend</Tip><SortIcon active={sk==="_ti"} asc={asc} /></th>
              <th style={thS("_mo",false)}   onClick={()=>hs("_mo")}><Tip text={`Marge de Sécurité — Décote par rapport\nà la valeur intrinsèque estimée.\nPositif = titre sous-évalué`}>MoS</Tip><SortIcon active={sk==="_mo"} asc={asc} /></th>
            </tr>
          </thead>
          <tbody>
            {slice.map((c,i)=>{
              const sig=computeSignal(c);
              const ss=SIG_STYLE[sig];
              const mv=fmtMoVal(c);
              const region=getRegion(c);
              const secShort=SEC_SHORT(c.sector);
              const stSc=stT(c.stock_style);
              const capShort=c.cap.replace(" Cap","");
              const capCol=CAP_C[c.cap]||C.gold;
              return (
                <tr key={c.symbol+i}
                  onMouseEnter={e=>e.currentTarget.style.background="rgba(181,154,91,.07)"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <td style={{...TD,minWidth:90}}>
                    <TickerSymbolLink symbol={c.symbol} onOpen={onOpenTicker} style={{ fontSize: 11 }} />
                  </td>
                  <td style={{...TD,minWidth:110}}>
                    <div style={{display:"flex",alignItems:"center",gap:4}}>
                      <div style={{width:6,height:6,borderRadius:1,background:REGION_COLOR_MAP[region]||C.gold,flexShrink:0}}/>
                      <span style={{fontSize:9,color:"var(--muted)"}}>{region}</span>
                    </div>
                  </td>
                  <td style={TD}>
                    <span style={{...MONO,fontSize:10,fontWeight:700,color:"#1f2a24"}}>{c.market}</span>
                  </td>
                  <td style={{...TD,minWidth:80}}>
                    <div style={{display:"flex",alignItems:"center",gap:4}}>
                      <div style={{width:6,height:6,borderRadius:1,background:SECTOR_C[c.sector]||C.gold,flexShrink:0}}/>
                      <span style={{fontSize:9,color:"var(--muted)"}}>{secShort}</span>
                    </div>
                  </td>
                  <td style={{...TD,textAlign:"right"}}>
                    <span style={{fontSize:9,fontWeight:800,...MONO,color:capCol}}>{capShort}</span>
                  </td>
                  <td style={{...TD,textAlign:"right"}}>
                    <span style={{fontSize:8,fontWeight:800,padding:"2px 5px",borderRadius:4,background:stSc.bg,border:`1px solid ${stSc.bd}`,color:stSc.col,whiteSpace:"nowrap"}}>{c.stock_style}</span>
                  </td>
                  <td style={{...TD,textAlign:"right"}}>
                    <span style={{fontSize:9,fontWeight:900,padding:"2px 7px",borderRadius:999,background:ss.bg,border:`1px solid ${ss.bd}`,color:ss.col,...MONO}}>{sig}</span>
                  </td>
                  <td style={{...TD,textAlign:"right",minWidth:60}}>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
                      <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,fontWeight:800,color:sC(c._is)}}>{fmtScore(c._is)}</span>
                      <div className="ct-pbar" style={{width:40}}><div className="ct-pbar-fill" style={{width:`${Number.isFinite(Number(c._is)) ? Math.min(100, Math.max(0, c._is)) : 0}%`,background:sC(c._is)}}/></div>
                    </div>
                  </td>
                  <td style={{...TD,textAlign:"right",minWidth:60}}>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
                      <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,fontWeight:800,color:sC(c._fn)}}>{fmtScore(c._fn)}</span>
                      <div className="ct-pbar" style={{width:40}}><div className="ct-pbar-fill" style={{width:`${Number.isFinite(Number(c._fn)) ? Math.min(100, Math.max(0, c._fn)) : 0}%`,background:sC(c._fn)}}/></div>
                    </div>
                  </td>
                  <td style={{...TD,textAlign:"right",minWidth:56}}>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
                      <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,fontWeight:800,color:sC(c._ti)}}>{fmtScore(c._ti, 0)}</span>
                      <div className="ct-pbar" style={{width:40}}><div className="ct-pbar-fill" style={{width:`${Number.isFinite(Number(c._ti)) ? Math.min(100, Math.max(0, c._ti)) : 0}%`,background:sC(c._ti)}}/></div>
                    </div>
                  </td>
                  <td style={{...TD,textAlign:"right",minWidth:56}}>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
                      <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,fontWeight:800,color:mosC(moNum(c))}}>{mv}</span>
                      <div className="ct-pbar" style={{width:40}}><div className="ct-pbar-fill" style={{width:`${Math.min(100,Math.max(0,moNum(c)))}%`,background:mosC(moNum(c))}}/></div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── DRAWER TABS ────────────────────────────────────────────── */
function AnalyseTab({ s, allYears }) {
  const fy=s.fyA??s.fyM; const hYrs=[...allYears].reverse(); const hMax=Math.max(...hYrs.map(y=>Math.abs((y.fyA??y.fyM)??0)),1);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {/* 2-col layout: trend line + monthly heatmap side by side */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <div>
          <Dsec>Tendance cumulative</Dsec>
          <div style={{background:"var(--paper-alt)",border:"1px solid var(--line)",borderRadius:10,padding:"10px 12px"}}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:"var(--muted)",marginBottom:6}}>
              <span style={{fontWeight:800,color:"var(--ink)"}}>Cumul mensuel</span>
              <span>{s.monthly.filter(d=>d.val!=null).length} mois</span>
            </div>
            <LineSVG monthly={s.monthly}/>
          </div>
        </div>
        <div>
          <Dsec>Rendements mensuels</Dsec>
          <div className="isp-mth-grid">
            {s.monthly.map((d,i)=>{
              const in2=d.val!=null?Math.min(.65,Math.abs(d.val)/30):0;
              const bg=d.val==null?"var(--paper-alt)":d.val>=0?hA(C.green,in2):hA(C.red,in2);
              return <div key={i} style={{background:bg,border:"1px solid var(--line2)",borderRadius:6,padding:"4px 2px",textAlign:"center"}}>
                <div style={{fontSize:7,color:"var(--muted)"}}>{d.m}</div>
                <div style={{...MONO,fontSize:10,fontWeight:800,color:d.val!=null?pC(d.val):"var(--muted)"}}>{d.val!=null?fP(d.val,1):"—"}</div>
              </div>;
            })}
          </div>
        </div>
      </div>
      {/* CAGR left + Risque right */}
      <div style={{display:"grid",gridTemplateColumns:"1.1fr 1fr",gap:10,alignItems:"start"}}>
        <div>
          <Dsec>CAGR multi-horizons</Dsec>
          <div style={{background:"var(--paper-alt)",border:"1px solid var(--line)",borderRadius:10,padding:"10px 12px",marginBottom:8}}>
            <div style={{fontSize:9,color:"var(--muted)",marginBottom:6}}>Rendement annualisé composé</div>
            <CagrSVG s={s}/>
          </div>
          <div className="mk4">{[["2Y",s.c2],["3Y",s.c3],["4Y",s.c4],["5Y",s.c5]].map(([l,v])=><MK key={l} label={"CAGR "+l} value={fP(v)} color={v!=null?pC(v):"var(--muted)"}/>)}</div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <Dsec>Risque & Volatilité</Dsec>
          <div className="mk3">
            {[["MDD 1Y",fP(s.mdd1),s.mdd1!=null&&s.mdd1<0?C.red:"var(--muted)"],["MDD 3Y",fP(s.mdd3),s.mdd3!=null&&s.mdd3<0?C.red:"var(--muted)"],["Vol. 1Y",s.s1?s.s1.toFixed(1)+"%":"—",C.amber]].map(([l,v,c])=><MK key={l} label={l} value={v} color={c}/>)}
          </div>
          <div className="mk5">
            {[["1Y",s.sh1],["2Y",s.sh2],["3Y",s.sh3],["4Y",s.sh4],["5Y",s.sh5]].map(([l,v])=><MK key={l} label={"Sh."+l} value={v!=null?v.toFixed(2):"—"} color={shC(v)}/>)}
          </div>
          {/* Synthèse */}
          <div style={{background:"var(--paper-alt)",borderRadius:10,border:"1px solid var(--line)",padding:"10px 12px",fontSize:11,lineHeight:1.65}}>
            <strong style={{color:"var(--ink)"}}>Synthèse : </strong>
            <span style={{color:"var(--muted)"}}>FY {fP(fy)} ({s.analYear}).{s.c3!=null?` CAGR 3Y : ${fP(s.c3)}.`:""}{s.sh1!=null?` Sharpe 1Y : ${s.sh1.toFixed(2)} (${s.sh1>1?"attractif":s.sh1>0?"acceptable":"insuffisant"}).`:""}{s.mdd1!=null?` MDD 1Y : ${fP(s.mdd1)}.`:""}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── FILTER CHIP ────────────────────────────────────────────── */
function FChip({ label, count, color, active, onClick }) {
  const bg = active ? color : "var(--paper-alt)";
  const col = active ? "#fff" : "var(--ink)";
  const bd = active ? color : "var(--line)";
  return (
    <span className={`fchip${active?" on":""}`}
      style={{background:bg,color:col,border:`1.5px solid ${bd}`}}
      onClick={onClick}>
      <span style={{width:7,height:7,borderRadius:2,background:active?"rgba(255,255,255,.5)":color,display:"inline-block",flexShrink:0}}/>
      {label}
      <span style={{opacity:active?0.75:0.55,fontWeight:600}}>({count})</span>
    </span>
  );
}

/* ─── COMPOSITION TAB (charts + filtres interactifs) ─────────── */
function CompoChartTab({ comp, filters, onFilterToggle, onClearFilters }) {
  if(!comp.length) return <div style={{background:"var(--paper-alt)",borderRadius:10,border:"1px solid var(--line)",padding:"16px 14px",fontSize:12,color:"var(--muted)"}}>Aucune donnée de composition disponible.</div>;
  const total=comp.length;
  const agg=(key,mapFn)=>{
    const m={};
    comp.forEach(c=>{
      const k=mapFn?(key?mapFn(c[key]):mapFn(c)):c[key];
      if(k==null||k==="") return;
      m[k]=(m[k]||0)+1;
    });
    return Object.entries(m).sort((a,b)=>b[1]-a[1]);
  };
  const regions=agg(null,c=>getRegion(c));
  const caps=agg("cap");
  const styles=agg("stock_style");
  const mkts=agg("market");

  const hasFilters=Object.values(filters).some(s=>s.size>0);

  // Bar click handler
  function handleBarClick(dim, val) { onFilterToggle(dim, val); }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>

      {/* Active filter summary */}
      {hasFilters && (
        <div className="filter-bar">
          <span className="filter-bar-label">Filtres actifs :</span>
          {Object.entries(filters).map(([dim,vals])=>
            [...vals].map(v=>{
              const dimLabel={region:"Région",cap:"Cap",style:"Style",market:"Marché"}[dim]||dim;
              return (
                <span key={dim+v} style={{display:"inline-flex",alignItems:"center",gap:4,background:"rgba(181,154,91,.15)",border:"1px solid rgba(181,154,91,.4)",borderRadius:999,padding:"2px 8px",fontSize:10,fontWeight:800,color:C.gold}}>
                  {dimLabel}: {v}
                  <button onClick={()=>onFilterToggle(dim,v)} style={{background:"none",border:"none",cursor:"pointer",color:C.gold,fontSize:11,padding:0,lineHeight:1}}>×</button>
                </span>
              );
            })
          )}
          <button className="filter-clear" onClick={onClearFilters}>↺ Reset</button>
        </div>
      )}

      {/* Régions + Capitalisation */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>

        {/* Régions — replaces Secteurs */}
        <div style={{background:"var(--paper-alt)",border:"1px solid var(--line)",borderRadius:12,padding:"12px 10px"}}>
          <div style={{fontSize:10,fontWeight:800,color:"var(--ink)",marginBottom:8}}>Régions</div>
          <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
            <DonutSVG items={regions.map(([n,c])=>[n,c,REGION_COLOR_MAP[n]||C.gold])} total={total} size={80}/>
            <div style={{flex:1,display:"flex",flexDirection:"column",gap:4}}>
              {regions.map(([n,c])=>{
                const on=filters.region?.has(n);
                return (
                  <div key={n} onClick={()=>handleBarClick("region",n)}
                    style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",borderRadius:6,padding:"2px 4px",background:on?hA(REGION_COLOR_MAP[n]||C.gold,.12):"transparent",border:on?`1px solid ${hA(REGION_COLOR_MAP[n]||C.gold,.35)}`:"1px solid transparent",transition:"all .15s"}}>
                    <div style={{width:7,height:7,borderRadius:2,background:REGION_COLOR_MAP[n]||C.gold,flexShrink:0}}/>
                    <span style={{fontSize:10,color:on?"var(--ink)":"var(--muted)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:on?800:500}}>{n}</span>
                    <span style={{...MONO,fontSize:10,color:"var(--muted)",fontWeight:700}}>{c}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Capitalisation */}
        <div style={{background:"var(--paper-alt)",border:"1px solid var(--line)",borderRadius:12,padding:"12px 10px"}}>
          <div style={{fontSize:10,fontWeight:800,color:"var(--ink)",marginBottom:8}}>Capitalisation</div>
          <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
            <DonutSVG items={caps.map(([n,c])=>[n,c,CAP_C[n]||C.gold])} total={total} size={80}/>
            <div style={{flex:1,display:"flex",flexDirection:"column",gap:4}}>
              {caps.map(([n,c])=>{
                const on=filters.cap?.has(n);
                return (
                  <div key={n} onClick={()=>handleBarClick("cap",n)}
                    style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",borderRadius:6,padding:"2px 4px",background:on?hA(CAP_C[n]||C.gold,.12):"transparent",border:on?`1px solid ${hA(CAP_C[n]||C.gold,.35)}`:"1px solid transparent",transition:"all .15s"}}>
                    <div style={{width:7,height:7,borderRadius:2,background:CAP_C[n]||C.gold,flexShrink:0}}/>
                    <span style={{fontSize:10,color:on?"var(--ink)":"var(--muted)",flex:1,fontWeight:on?800:500}}>{n}</span>
                    <span style={{...MONO,fontSize:10,color:"var(--muted)",fontWeight:700}}>{c}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Répartition régionale — barres cliquables */}
      <div style={{background:"var(--paper-alt)",border:"1px solid var(--line)",borderRadius:12,padding:"12px 14px"}}>
        <div style={{fontSize:10,fontWeight:800,color:"var(--ink)",marginBottom:10}}>
          Répartition régionale <span style={{fontSize:9,color:"var(--muted)",fontWeight:600}}>{total} titres</span>
          <span style={{fontSize:9,color:"var(--muted)",fontWeight:500,marginLeft:8}}>Cliquez pour filtrer les constituants</span>
        </div>
        {regions.map(([n,c])=>{
          const on=filters.region?.has(n);
          const col=REGION_COLOR_MAP[n]||C.gold;
          return (
            <div key={n} onClick={()=>handleBarClick("region",n)}
              style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,cursor:"pointer",borderRadius:6,padding:"3px 6px",background:on?hA(col,.08):"transparent",border:on?`1px solid ${hA(col,.3)}`:"1px solid transparent",transition:"all .15s"}}>
              <div style={{fontSize:10,color:on?"var(--ink)":"var(--muted)",width:150,flexShrink:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:on?800:500}}>{n}</div>
              <div style={{flex:1,height:9,borderRadius:999,background:"var(--line2)",overflow:"hidden"}}>
                <div style={{height:"100%",width:`${c/total*100}%`,background:col,borderRadius:999,opacity:on?1:.7}}/>
              </div>
              <div style={{...MONO,fontSize:10,fontWeight:700,color:on?col:"var(--muted)",width:28,textAlign:"right",flexShrink:0}}>{c}</div>
            </div>
          );
        })}
      </div>

      {/* Marchés + Styles */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        {/* Marchés */}
        <div style={{background:"var(--paper-alt)",border:"1px solid var(--line)",borderRadius:12,padding:"12px 10px"}}>
          <div style={{fontSize:10,fontWeight:800,color:"var(--ink)",marginBottom:8}}>Marchés</div>
          <DonutSVG items={mkts.map(([n,c])=>[n,c,avC(n)])} total={total} size={80}/>
          <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:3}}>
            {mkts.slice(0,8).map(([n,c])=>{
              const on=filters.market?.has(n);
              return (
                <div key={n} onClick={()=>handleBarClick("market",n)}
                  style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",borderRadius:5,padding:"2px 4px",background:on?hA(avC(n),.12):"transparent",border:on?`1px solid ${hA(avC(n),.35)}`:"1px solid transparent",transition:"all .12s"}}>
                  <div style={{width:6,height:6,borderRadius:2,background:avC(n),flexShrink:0}}/>
                  <span style={{fontSize:10,color:on?"var(--ink)":"var(--muted)",flex:1,fontWeight:on?800:500}}>{n}</span>
                  <span style={{...MONO,fontSize:10,color:"var(--muted)",fontWeight:700}}>{Math.round(c/total*100)}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Styles */}
        <div style={{background:"var(--paper-alt)",border:"1px solid var(--line)",borderRadius:12,padding:"12px 10px"}}>
          <div style={{fontSize:10,fontWeight:800,color:"var(--ink)",marginBottom:8}}>Styles de sélection</div>
          <DonutSVG items={styles.map(([n,c])=>[n,c,stT(n).col])} total={total} size={80}/>
          <div style={{marginTop:8,display:"flex",flexWrap:"wrap",gap:5}}>
            {styles.map(([st,c])=>{
              const sc=stT(st);
              const on=filters.style?.has(st);
              return (
                <FChip key={st} label={st} count={c}
                  color={sc.col} active={on}
                  onClick={()=>handleBarClick("style",st)}/>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── CONSTITUANTS TAB (filtrée) ─────────────────────────────── */
function ConstituantsTab({ comp }) {
  const [fRegion,setFRegion]=useState(null);
  const [fMarket,setFMarket]=useState(null);
  const [fSector,setFSector]=useState(null);
  const [fCap,setFCap]=useState(null);
  const [fStyle,setFStyle]=useState(null);
  const [fSig,setFSig]=useState(null);
  const [search,setSearch]=useState("");

  const filtered=useMemo(()=>{
    let r=[...comp];
    if(fRegion) r=r.filter(c=>getRegion(c)===fRegion);
    if(fMarket) r=r.filter(c=>c.market===fMarket);
    if(fSector) r=r.filter(c=>c.sector===fSector);
    if(fCap)    r=r.filter(c=>c.cap===fCap);
    if(fStyle)  r=r.filter(c=>c.stock_style===fStyle);
    if(fSig)    r=r.filter(c=>computeSignal(c)===fSig);
    if(search){ const q=search.toLowerCase(); r=r.filter(c=>c.symbol.toLowerCase().includes(q)||c.sector.toLowerCase().includes(q)||c.market.toLowerCase().includes(q)); }
    return r;
  },[comp,fRegion,fMarket,fSector,fCap,fStyle,fSig,search]);

  const hasFilter=fRegion||fMarket||fSector||fCap||fStyle||fSig||search;
  function clearAll(){setFRegion(null);setFMarket(null);setFSector(null);setFCap(null);setFStyle(null);setFSig(null);setSearch("");}

  // Unique values for dropdowns (from full comp, not filtered)
  const uniq=(key,mapFn)=>{
    const vals=comp.map(c=>(mapFn?mapFn(c):c[key])).filter(v=>v!=null&&v!=="");
    return [...new Set(vals)].sort((a,b)=>String(a).localeCompare(String(b),"fr"));
  };
  const regions=uniq(null,c=>getRegion(c));
  const markets=uniq("market");
  const sectors=uniq("sector");
  const caps=["Mega Cap","Large Cap","Mid Cap","Small Cap","Nano Cap"].filter(v=>comp.some(c=>c.cap===v));
  const styles=uniq("stock_style");

  if(!comp.length) return (
    <div style={{background:"var(--paper-alt)",borderRadius:10,border:"1px solid var(--line)",padding:"16px 14px",fontSize:12,color:"var(--muted)"}}>
      Aucune donnée de composition disponible.
    </div>
  );
  return (
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {/* Filter bar */}
      <div className="isp-fbar">
        <span className="isp-fbar-label">Filtres</span>
        <div style={{position:"relative",flexShrink:0}}>
          <span style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)",color:"#a0a89e",fontSize:11,pointerEvents:"none"}}>⌕</span>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Symbole…"
            className={`isp-fsearch${search?" has-value":""}`}/>
        </div>
        <select value={fRegion||""} onChange={e=>setFRegion(e.target.value||null)} className={`isp-fsel${fRegion?" active":""}`}>
          <option value="">Région</option>{regions.map(r=><option key={r}>{r}</option>)}
        </select>
        <select value={fMarket||""} onChange={e=>setFMarket(e.target.value||null)} className={`isp-fsel${fMarket?" active":""}`}>
          <option value="">Marché</option>{markets.map(r=><option key={r}>{r}</option>)}
        </select>
        <select value={fSector||""} onChange={e=>setFSector(e.target.value||null)} className={`isp-fsel${fSector?" active":""}`}>
          <option value="">Secteur</option>{sectors.map(r=><option key={r} value={r}>{SEC_SHORT(r)}</option>)}
        </select>
        <select value={fCap||""} onChange={e=>setFCap(e.target.value||null)} className={`isp-fsel${fCap?" active":""}`}>
          <option value="">Cap</option>{caps.map(r=><option key={r}>{r}</option>)}
        </select>
        <select value={fStyle||""} onChange={e=>setFStyle(e.target.value||null)} className={`isp-fsel${fStyle?" active":""}`}>
          <option value="">Style</option>{styles.map(r=><option key={r}>{r}</option>)}
        </select>
        <select value={fSig||""} onChange={e=>setFSig(e.target.value||null)} className={`isp-fsel${fSig?" active":""}`}>
          <option value="">Signal</option>
          {["BUY","HOLD","WATCH"].map(s=><option key={s}>{s}</option>)}
        </select>
        {hasFilter&&<span style={{fontSize:10,color:"#7a8a82",...MONO,marginLeft:"auto"}}>{filtered.length} / {comp.length}</span>}
        {hasFilter&&<button onClick={clearAll} style={{border:"1px solid rgba(181,154,91,.35)",borderRadius:7,background:"#fffaed",color:C.gold,fontSize:10,fontWeight:800,cursor:"pointer",...MONO,padding:"4px 10px"}}>↺ Reset</button>}
      </div>
      <CTTable comp={filtered}/>
    </div>
  );
}

/* ─── STRATEGY MODAL (centered overlay) ─────────────────────── */
function StrategyPage({ stratId, allS, allComp, onClose, onInvestor }) {
  const { stratInv, onRequestConstituents } = useStrategiesData();
  const [tab,setTab]=useState("constituants");
  const [isMob,setIsMob]=useState(()=>window.innerWidth<=720);
  const [compLoading, setCompLoading] = useState(false);
  useEffect(()=>{
    document.body.style.overflow="hidden";
    const fn=()=>setIsMob(window.innerWidth<=720);
    window.addEventListener("resize",fn);
    const onKey=e=>{if(e.key==="Escape")onClose();};
    window.addEventListener("keydown",onKey);
    return()=>{document.body.style.overflow="";window.removeEventListener("resize",fn);window.removeEventListener("keydown",onKey);};
  },[]);
  const s=allS.find(x=>x.id===stratId);

  useEffect(() => {
    if (!s?.name || !onRequestConstituents) return;
    const n = allComp.filter((c) => c.strategy === s.name).length;
    if (n > 0) {
      setCompLoading(false);
      return;
    }
    let cancelled = false;
    setCompLoading(true);
    Promise.resolve(onRequestConstituents(s.name)).finally(() => {
      if (!cancelled) setCompLoading(false);
    });
    return () => { cancelled = true; };
  }, [s?.name, onRequestConstituents, allComp]);

  if(!s) return null;
  const fy=s.fyA??s.fyM; const iName=stratInv[s.name];
  const allYears=allS.filter(x=>x.name===s.name).sort((a,b)=>b.analYear-a.analYear);
  const comp=allComp.filter(c=>c.strategy===s.name).map(parseComp);

  const mets=[[fP(fy),"PERF. FY",fy!=null?pC(fy):"#fff",26]];
  if(s.c3!=null)mets.push([fP(s.c3),"CAGR 3Y",pC(s.c3),18]);
  if(s.sh1!=null)mets.push([s.sh1.toFixed(2),"SHARPE 1Y",shC(s.sh1),18]);
  if(s.mdd1!=null)mets.push([fP(s.mdd1),"MDD 1Y",pC(s.mdd1),16]);
  return (
    <ModalPortal>
    <div className={`isp-moverlay${isMob?" mobile":""}`} onMouseDown={onClose}>
      <div className={`isp-mbox${isMob?" mobile":""}`} onMouseDown={e=>e.stopPropagation()}>
        {/* White header — strategy name as title */}
        <div className="isp-mhd">
          <button type="button" className="isp-mclose" onClick={onClose} aria-label="Fermer">×</button>
          {/* Strategy name — prominent title */}
          <div style={{...SERIF,fontSize:"clamp(18px,2.2vw,26px)",fontWeight:700,color:"#1f2a24",lineHeight:1.2,paddingRight:52,marginBottom:10}}>{s.name}</div>
          {/* Metrics row */}
          <div style={{display:"flex",gap:20,flexWrap:"wrap",alignItems:"flex-end"}}>
            {mets.map(([v,l,c,sz])=>(
              <div key={l}>
                <div style={{...MONO,fontWeight:800,fontSize:sz,color:c,lineHeight:1}}>{v}</div>
                <div style={{display:"flex",alignItems:"center",gap:4,marginTop:3}}>
                <span style={{fontSize:9,color:"#a0a89e",textTransform:"uppercase",letterSpacing:".11em",...MONO}}>{l}</span>
                {l==="PERF. FY"&&<TipDot text={"Performance FY : rendement total\nde la stratégie sur l\u2019année fiscale"} down={true}/>}
                {l==="CAGR 3Y"&&<TipDot text={"Croissance annuelle composée\nsur 3 ans. Indique la tendance\nde long terme de la stratégie"} down={true}/>}
                {l==="SHARPE 1Y"&&<TipDot text={"Rendement ajusté du risque.\n>1 = bon · >2 = excellent\nCompare gain vs volatilité"} down={true}/>}
                {l==="MDD 1Y"&&<TipDot text={"Perte maximale depuis un sommet\nSur 1 an. Plus proche de 0%,\nmoins risquée est la stratégie"} down={true}/>}
              </div>
              </div>
            ))}
            {/* Investisseur chip — right side */}
            {iName&&<span onClick={()=>onInvestor(iName)}
              style={{marginLeft:"auto",display:"inline-flex",alignItems:"center",gap:6,flexShrink:0,
                cursor:"pointer",padding:"5px 10px",borderRadius:8,
                border:"1px solid rgba(30,40,34,.10)",background:"#f5f3ee",transition:"all .15s"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=C.gold;e.currentTarget.style.background="#fffaed";}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(30,40,34,.10)";e.currentTarget.style.background="#f5f3ee";}}>
              <Avatar name={iName} size={18}/>
              <span style={{fontSize:11,color:"#1f2a24",fontWeight:700}}>{iName}</span>
              <span style={{fontSize:10,color:C.gold,...MONO,fontWeight:800}}>→</span>
            </span>}
          </div>
        </div>
        {/* Constituants label bar */}
        <div className="isp-mtabs">
          <span style={{padding:"9px 0",fontSize:10,fontWeight:800,color:"#1f2a24",...MONO,textTransform:"uppercase",letterSpacing:".10em",opacity:.55}}>Constituants</span>
        </div>
        <div className="isp-mbody">
          {compLoading && comp.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
              Chargement des constituants…
            </div>
          ) : (
            <ConstituantsTab comp={comp}/>
          )}
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}

/* ─── INVESTOR MODAL (centered overlay) ─────────────────────── */
function InvestorPage({ name, allS, onClose, onStrategy }) {
  const { stratInv, investors } = useStrategiesData();
  const [isMob,setIsMob]=useState(()=>window.innerWidth<=720);
  useEffect(()=>{
    document.body.style.overflow="hidden";
    const fn=()=>setIsMob(window.innerWidth<=720);
    window.addEventListener("resize",fn);
    const onKey=e=>{if(e.key==="Escape")onClose();};
    window.addEventListener("keydown",onKey);
    return()=>{document.body.style.overflow="";window.removeEventListener("resize",fn);window.removeEventListener("keydown",onKey);};
  },[]);
  const inv=investors[name]; if(!inv) return null;
  const col=avC(name); const sc=stT(inv.style);
  const strats=Object.values(allS.reduce((m,s)=>{if(stratInv[s.name]===name&&(!m[s.name]||s.analYear>m[s.name].analYear))m[s.name]=s;return m;},{})).sort((a,b)=>((b.fyA??b.fyM)??-Infinity)-((a.fyA??a.fyM)??-Infinity));
  return (
    <ModalPortal>
    <div className={`isp-moverlay${isMob?" mobile":""}`} onMouseDown={onClose}>
      <div className={`isp-mbox${isMob?" mobile":""}`} style={{maxWidth:"min(720px,94vw)"}} onMouseDown={e=>e.stopPropagation()}>
        {/* White header */}
        <div className="isp-mhd">
          <button type="button" className="isp-mclose" onClick={onClose} aria-label="Fermer">×</button>
          <div style={{display:"flex",alignItems:"center",gap:14,paddingRight:52}}>
            <div style={{width:44,height:44,borderRadius:"50%",background:hA(col,.15),color:col,border:`2px solid ${hA(col,.35)}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:900,...MONO,flexShrink:0}}>{(inv.ini || inv.name || "?").slice(0, 2)}</div>
            <div>
              <div style={{...SERIF,fontSize:"clamp(18px,2.5vw,24px)",fontWeight:700,color:"#18221e",letterSpacing:"-.02em",lineHeight:1.2}}>{name}</div>
              <span style={{display:"inline-flex",marginTop:5,fontSize:10,fontWeight:800,padding:"2px 10px",borderRadius:999,background:hA(sc.col,.12),border:`1px solid ${hA(sc.col,.3)}`,color:sc.col}}>{inv.style}</span>
            </div>
          </div>
        </div>
        {/* Scrollable body */}
        <div className="isp-mbody" style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{...card,overflow:"hidden"}}>
            <div style={{padding:"14px 16px",background:"var(--paper-alt)",borderBottom:"1px solid var(--line)"}}>
              <div style={{fontSize:8,fontWeight:800,textTransform:"uppercase",letterSpacing:".12em",color:C.gold,marginBottom:7}}>Biographie</div>
              <div style={{fontSize:12,color:"var(--ink)",lineHeight:1.8,fontWeight:500}}>{inv.bio}</div>
            </div>
            <div style={{padding:"14px 16px"}}>
              <div style={{fontSize:8,fontWeight:800,textTransform:"uppercase",letterSpacing:".12em",color:col,marginBottom:7}}>Philosophie d'investissement</div>
              <div style={{fontSize:12,color:"var(--ink)",lineHeight:1.8,fontWeight:600}}>{inv.philosophy}</div>
            </div>
          </div>
          {strats.length>0&&<div style={{...card,...cardPad}}>
            <div style={{fontSize:15,fontWeight:900,color:"var(--ink)",letterSpacing:"-.02em",marginBottom:14}}>Stratégies associées</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {strats.map(s=>{const v=s.fyA??s.fyM;return(
                <div key={s.id} onClick={()=>onStrategy(s.id)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"10px 14px",background:"var(--paper-alt)",borderRadius:10,border:"1px solid var(--line)",cursor:"pointer",transition:"background .12s"}} onMouseEnter={e=>e.currentTarget.style.background="var(--row-h)"} onMouseLeave={e=>e.currentTarget.style.background="var(--paper-alt)"}>
                  <div><div style={{fontSize:13,fontWeight:800,color:"var(--ink)"}}>{s.name}</div><div style={{fontSize:9,color:"var(--muted)",...MONO,marginTop:2}}>{s.selYear}→{s.analYear}</div></div>
                  <div style={{display:"flex",alignItems:"center",gap:14,flexShrink:0}}>
                    <div style={{textAlign:"right"}}><div style={{...MONO,fontSize:15,fontWeight:800,color:v!=null?pC(v):"var(--muted)"}}>{fP(v)}</div><div style={{fontSize:9,color:"var(--muted)"}}>FY</div></div>
                    {s.c3!=null&&<div style={{textAlign:"right"}}><div style={{...MONO,fontSize:13,fontWeight:800,color:pC(s.c3)}}>{fP(s.c3)}</div><div style={{fontSize:9,color:"var(--muted)"}}>CAGR 3Y</div></div>}
                  </div>
                </div>
              );})}
            </div>
          </div>}
        </div>
        <div className="isp-mfooter">Appuie sur <b>Esc</b> ou clique en dehors pour fermer</div>
      </div>
    </div>
    </ModalPortal>
  );
}


/* ─── TOOLTIP (portal — non coupé par thead / overflow) ─────── */
function Tip({ text, children }) {
  return (
    <span className="isp-tip-inline">
      {children}
      <InvelpsTipMark title={text} down />
    </span>
  );
}
function TipDot({ text, down = false }) {
  return <InvelpsTipMark title={text} down={down} />;
}

/* ─── SPARKBAR MODAL (monthly bar chart) ─────────────────────── */
function BarChartSVG({ monthly, year, height=100 }) {
  const W=440, H=height, pad=20;
  const barW=Math.floor((W-pad*2)/12)-3;
  const zeroY=H*0.6; // zero slightly below center (more room for positive)
  const vals=monthly.map(d=>d.val??null);
  const defined=vals.filter(v=>v!=null);
  const maxPos=Math.max(...defined.filter(v=>v>0),0.01);
  const maxNeg=Math.max(...defined.filter(v=>v<0).map(Math.abs),0.01);
  const posH=zeroY-8;
  const negH=H-zeroY-8;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H+16}`} style={{overflow:"visible",display:"block"}}>
      <line x1={pad} y1={zeroY} x2={W-pad} y2={zeroY} stroke="rgba(30,40,34,.12)" strokeWidth={1} strokeDasharray="3,2"/>
          {vals.map((v,i)=>{
            if(v==null) return null;
            const x=pad+i*(barW+3);
            const col=v>=0?C.green:C.red;
        const bh=v>=0?Math.max(2,v/maxPos*posH):Math.max(2,Math.abs(v)/maxNeg*negH);
        const y=v>=0?zeroY-bh:zeroY;
            return (
              <g key={i}>
            <rect x={x} y={y} width={barW} height={bh} fill={col} opacity={.85} rx={2}/>
            <text x={x+barW/2} y={H+14} textAnchor="middle"
              style={{fontSize:7.5,fill:"var(--muted)",...MONO}}>{MONTHS[i].slice(0,1)}</text>
            <title>{MONTHS[i]} {year}: {fP(v)}</title>
              </g>
            );
          })}
      {/* Value labels — only show if bar is tall enough */}
          {vals.map((v,i)=>{
        if(v==null||Math.abs(v)<0.5) return null;
            const x=pad+i*(barW+3)+barW/2;
        const bh=v>=0?Math.max(2,v/maxPos*posH):Math.max(2,Math.abs(v)/maxNeg*negH);
        const yLbl=v>=0?zeroY-bh-4:zeroY+bh+9;
        return <text key={i} x={x} y={yLbl} textAnchor="middle"
          style={{fontSize:7,fill:v>=0?C.green:C.red,...MONO,fontWeight:700}}>{fP(v,0)}</text>;
          })}
        </svg>
  );
}

function SparkModal({ s, allS, onClose }) {
  useEffect(()=>{
    const fn=e=>{if(e.key==="Escape")onClose();};
    window.addEventListener("keydown",fn);
    return()=>window.removeEventListener("keydown",fn);
  },[]);

  // Current year = s.analYear, prev year = analYear - 1
  const prevYear = s.analYear - 1;
  const sPrev = allS?.find(x=>x.name===s.name && x.analYear===prevYear);

  const fyC = s.fyA??s.fyM;
  const fyP = sPrev ? (sPrev.fyA??sPrev.fyM) : null;

  return (
    <ModalPortal>
    <div className="isp-moverlay" onMouseDown={onClose}>
      <div className="isp-mbox" style={{width:"min(600px,94vw)",maxHeight:"min(88vh,820px)"}} onMouseDown={e=>e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="isp-mhd" style={{paddingBottom:12}}>
          <button type="button" className="isp-mclose" onClick={onClose} aria-label="Fermer">×</button>
          <div style={{paddingRight:52}}>
            <div style={{...SERIF,fontSize:17,fontWeight:700,color:"#1f2a24",lineHeight:1.2}}>{s.name}</div>
            <div style={{fontSize:10,color:"var(--muted)",marginTop:3,...MONO}}>Rendements mensuels</div>
          </div>
          {/* FY performance badges — spark modal */}
          <div style={{display:"flex",gap:10,marginTop:10,flexWrap:"wrap"}}>
            <div style={{background:"rgba(30,40,34,.04)",border:"1px solid rgba(30,40,34,.08)",
                borderRadius:9,padding:"6px 12px",display:"flex",flexDirection:"column",gap:2}}>
              <span style={{fontSize:8,color:"var(--muted)",...MONO,textTransform:"uppercase",letterSpacing:".10em"}}>
                Perf. FY {s.analYear}
              </span>
              <span style={{...MONO,fontSize:18,fontWeight:800,color:fyC!=null?pC(fyC):"var(--muted)"}}>{fP(fyC)}</span>
            </div>
            {fyP!=null&&<div style={{background:"rgba(30,40,34,.04)",border:"1px solid rgba(30,40,34,.08)",
                borderRadius:9,padding:"6px 12px",display:"flex",flexDirection:"column",gap:2}}>
              <span style={{fontSize:8,color:"var(--muted)",...MONO,textTransform:"uppercase",letterSpacing:".10em"}}>
                Perf. FY {prevYear}
              </span>
              <span style={{...MONO,fontSize:18,fontWeight:800,color:pC(fyP)}}>{fP(fyP)}</span>
            </div>}
          </div>
        </div>

        {/* ── Body — deux bar charts empilés ── */}
        <div style={{padding:"16px 20px 20px",display:"flex",flexDirection:"column",gap:16}}>

          {/* Année en cours */}
          <div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
              <span style={{fontSize:10,fontWeight:800,color:"#1f2a24",...MONO}}>
                {s.analYear} <span style={{fontWeight:500,color:"var(--muted)"}}>· {s.monthly.filter(d=>d.val!=null).length} mois</span>
              </span>
              <span style={{...MONO,fontSize:13,fontWeight:800,color:fyC!=null?pC(fyC):"var(--muted)"}}>{fP(fyC)}</span>
            </div>
            <div style={{background:"#f9f8f5",borderRadius:10,border:"1px solid rgba(30,40,34,.07)",padding:"14px 12px 6px"}}>
              <BarChartSVG monthly={s.monthly} year={s.analYear} height={120}/>
            </div>
          </div>

          {/* Divider */}
          {sPrev&&<div style={{height:1,background:"rgba(30,40,34,.07)"}}/>}

          {/* Année précédente */}
          {sPrev&&<div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
              <span style={{fontSize:10,fontWeight:800,color:"#1f2a24",...MONO}}>
                {prevYear} <span style={{fontWeight:500,color:"var(--muted)"}}>· 12 mois</span>
              </span>
              <span style={{...MONO,fontSize:13,fontWeight:800,color:pC(fyP)}}>{fP(fyP)}</span>
            </div>
            <div style={{background:"#f9f8f5",borderRadius:10,border:"1px solid rgba(30,40,34,.07)",padding:"14px 12px 6px"}}>
              <BarChartSVG monthly={sPrev.monthly} year={prevYear} height={120}/>
            </div>
          </div>}

        </div>
      </div>
    </div>
    </ModalPortal>
  );
}

/* ─── STRATEGIES LIST PAGE ───────────────────────────────────── */
function StrategiesPage({ allS, allComp, onDrawer, onInvestor, drawerStratId, isMobile, view, setView }) {
  const { stratInv, investors } = useStrategiesData();
  const [search,setSearch]=useState(""); const [yearF,setYearF]=useState("all"); const [styleF,setStyleF]=useState("all");
  const [sk,setSk]=useState("fy"); const [asc,setAsc]=useState(false);
  const [sparkStrat,setSparkStrat]=useState(null);
  const getFY=s=>s.fyA??s.fyM;
  const latestMap=useMemo(()=>{const m={};allS.forEach(s=>{if(!m[s.name]||s.analYear>m[s.name].analYear)m[s.name]=s;});return Object.values(m);},[allS]);
  const years=useMemo(()=>[...new Set(allS.map(s=>s.analYear))].sort((a,b)=>b-a),[allS]);
  const istyles=useMemo(
    () => [...new Set(Object.values(investors).map((i) => i.style).filter(Boolean))].sort(),
    [investors]
  );
  const displayed=useMemo(()=>{
    let list=yearF==="all"?latestMap:allS.filter(s=>s.analYear===parseInt(yearF));
    if(styleF!=="all")list=list.filter(s=>investors[stratInv[s.name]]?.style===styleF);
    if(search){const q=search.toLowerCase();list=list.filter(s=>s.name.toLowerCase().includes(q)||(stratInv[s.name]||"").toLowerCase().includes(q));}
    const fns={fy:(a,b)=>(getFY(b)??-Infinity)-(getFY(a)??-Infinity),cagr3:(a,b)=>(b.c3??-Infinity)-(a.c3??-Infinity),cagr5:(a,b)=>(b.c5??-Infinity)-(a.c5??-Infinity),sh1:(a,b)=>(b.sh1??-Infinity)-(a.sh1??-Infinity),mdd1:(a,b)=>(b.mdd1??-Infinity)-(a.mdd1??-Infinity),name:(a,b)=>a.name.localeCompare(b.name)};
    const r=[...list].sort(fns[sk]||fns.fy); return asc?r.reverse():r;
  },[latestMap,allS,search,styleF,yearF,sk,asc,stratInv,investors]);
  const pos=displayed.filter(s=>(getFY(s)??0)>0).length;
  const avg=displayed.length?displayed.reduce((a,s)=>a+(getFY(s)??0),0)/displayed.length:0;
  const best=displayed.reduce((b,s)=>{const v=getFY(s)??-Infinity;return v>(b?.v??-Infinity)?{s,v}:b;},null);
  const hs=k=>{if(sk===k)setAsc(a=>!a);else{setSk(k);setAsc(false);}};
  const medal=i=>i===0?"🥇":i===1?"🥈":i===2?"🥉":String(i+1).padStart(2,"0");
  const iStyle={background:"var(--paper-alt)",border:"1px solid var(--line)",borderRadius:10,padding:"7px 10px",fontSize:12,color:"var(--ink)",...MONO,outline:"none"};
  return (
    <div className="isp-strategies-main" style={{display:"flex",flexDirection:"column",gap:16}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
        <div><h1 style={{...SERIF,fontSize:"clamp(26px,4vw,42px)",fontWeight:700,color:"var(--ink)",letterSpacing:"-.03em"}}>Index Strategies</h1><p style={{fontSize:12,color:"var(--muted)",fontWeight:600,marginTop:2}}>Backtests · Investisseurs · Composition</p></div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}><Bdg tone={avg>=0?"g":"r"}>Moy. FY {fP(avg,1)}</Bdg><Bdg>2025</Bdg></div>
      </div>
      {/* KPIs */}
      <div className="isp-kpi-grid">
        {[{l:"Stratégies",v:displayed.length,c:"var(--ink)",tip:"Nombre de stratégies d'investissement\naffichées selon les filtres actifs"},{l:"Positives",v:pos,c:C.green,tip:"Stratégies avec une performance\nannuelle positive (FY > 0%)"},{l:"Taux succès",v:displayed.length?Math.round(pos/displayed.length*100)+"%":"—",c:C.amber,tip:"% de stratégies positives.\nUn marché haussier dépasse souvent 70%"},{l:"Best FY",v:best?fP(best.v,0):"—",c:C.green,sub:best?.s.name.split(" ").slice(0,3).join(" ")}].map((kpi)=>{ const {l,v,c,sub}=kpi; return (
          <div key={l} style={{background:"var(--paper-alt)",border:"1px solid var(--line)",borderRadius:11,padding:"9px 11px"}}>
            <div style={{display:"flex",alignItems:"center",gap:4}}><span style={{fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".13em",color:"var(--muted)"}}>{l}</span>{kpi.tip&&<TipDot text={kpi.tip}/>}</div>
            <div className="isp-kpi-val" style={{...MONO,fontWeight:800,color:c,lineHeight:1,marginTop:3}}>{v}</div>
            {sub&&<div style={{fontSize:10,color:"var(--muted)",marginTop:2,fontWeight:600}}>{sub}</div>}
          </div>
        );})}
      </div>

      {/* Filters */}
      <div style={{background:"var(--paper)",border:"1px solid var(--line)",borderRadius:14,padding:"12px 14px",boxShadow:"0 2px 8px rgba(16,24,20,.04)"}}>
        <div style={{display:"flex",gap:7,flexWrap:"wrap",alignItems:"center"}}>
          {/* Search */}
          <div style={{position:"relative",flex:1,minWidth:160}}>
            <span style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)",color:"var(--muted)",fontSize:13,pointerEvents:"none"}}>⌕</span>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Stratégie ou investisseur…"
              style={{width:"100%",background:"#fff",border:"1px solid rgba(30,40,34,.12)",borderRadius:8,padding:"6px 10px 6px 28px",fontSize:13,color:"var(--ink)",...MONO,outline:"none"}}/>
          </div>
          {/* Style filter */}
          <select value={styleF} onChange={e=>setStyleF(e.target.value)}
            className={`isp-fsel${styleF!=="all"?" active":""}`}>
            <option value="all">Philosophie</option>
            {istyles.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          {/* Year filter */}
          <select value={yearF} onChange={e=>setYearF(e.target.value)}
            className={`isp-fsel${yearF!=="all"?" active":""}`}>
            <option value="all">Toutes les années</option>
            {years.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          {/* Sort */}
          <select value={sk} onChange={e=>{setSk(e.target.value);setAsc(false);}} className="isp-fsel">
            <option value="fy">Trier : FY</option>
            <option value="cagr3">Trier : CAGR 3Y</option>
            <option value="cagr5">Trier : CAGR 5Y</option>
            <option value="sh1">Trier : Sharpe 1Y</option>
            <option value="mdd1">Trier : MDD 1Y</option>
            <option value="name">Trier : Nom A–Z</option>
          </select>
          {(search||styleF!=="all"||yearF!=="all")&&
            <button onClick={()=>{setSearch("");setStyleF("all");setYearF("all");}}
              style={{border:"1px solid rgba(181,154,91,.35)",borderRadius:7,background:"#fffaed",color:C.gold,fontSize:10,fontWeight:800,cursor:"pointer",...MONO,padding:"5px 10px"}}>
              ↺ Reset
            </button>
          }
        </div>
        <div style={{display:"flex",gap:5,marginTop:9,flexWrap:"wrap",alignItems:"center"}}>
          {[["perf","Performance"],["risk","Risque"]].map(([k,l])=>(
            <button key={k} onClick={()=>setView(k)} style={{padding:"6px 16px",borderRadius:999,fontSize:13,fontWeight:700,cursor:"pointer",
              border:`1px solid ${view===k?"rgba(181,154,91,.5)":"rgba(30,40,34,.10)"}`,
              background:view===k?"rgba(181,154,91,.13)":"transparent",
              color:view===k?C.gold:"var(--muted)",...MONO,transition:"all .15s"}}>{l}</button>
          ))}
          <span style={{fontSize:10,color:"var(--muted)",marginLeft:"auto",...MONO}}>{displayed.length} stratégie{displayed.length>1?"s":""}</span>
        </div>
      </div>
      {sparkStrat&&<SparkModal s={sparkStrat} allS={allS} onClose={()=>setSparkStrat(null)}/>}
      {/* Strategy list */}
      <div style={{...card,overflow:"hidden"}}>
        {isMobile ? (
          <div style={{display:"flex",flexDirection:"column",gap:8,padding:"10px 12px"}}>
            {displayed.map((s,i)=>{const v=getFY(s);const iName=stratInv[s.name];const inv=investors[iName];const sc=inv?stT(inv.style):null;return(
              <div key={s.id} className={`isp-strat-card${drawerStratId===s.id?" selected":""}`} onClick={()=>onDrawer(s.id)}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:8}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:800,color:"var(--ink)"}}>{medal(i)} {s.name}</div>
                    <div style={{fontSize:9,color:"var(--muted)",...MONO,marginTop:2}}>{s.selYear}→{s.analYear}</div>
                    {iName&&<div style={{marginTop:6}}><InvChip name={iName} onNav={onInvestor}/></div>}
                  </div>
                  <div style={{...MONO,fontSize:18,fontWeight:800,lineHeight:1,flexShrink:0,color:v!=null?pC(v):"var(--muted)"}}>{fP(v)}</div>
                </div>
                <div onClick={e=>{e.stopPropagation();setSparkStrat(s);}} style={{cursor:"pointer"}} title="Voir le graphique mensuel"><Sparkbar monthly={s.monthly}/></div>
                <div className="isp-strat-kpis" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,marginTop:8}}>
                  {(view==="perf"?[["CAGR 3Y",fP(s.c3),s.c3!=null?pC(s.c3):"var(--muted)"],["CAGR 5Y",fP(s.c5),s.c5!=null?pC(s.c5):"var(--muted)"],["Sharpe 1Y",s.sh1!=null?s.sh1.toFixed(2):"—",shC(s.sh1)]]:[["MDD 1Y",fP(s.mdd1),s.mdd1!=null?pC(s.mdd1):"var(--muted)"],["Vol. 1Y",s.s1?s.s1.toFixed(1)+"%":"—",C.amber],["Sh. 3Y",s.sh3!=null?s.sh3.toFixed(2):"—",shC(s.sh3)]]).map(([l,v2,c])=>(
                    <div key={l} style={{background:"var(--paper-alt)",borderRadius:7,padding:"5px 7px",border:"1px solid var(--line2)"}}>
                      <div style={{fontSize:8,color:"var(--muted)",...MONO,textTransform:"uppercase",letterSpacing:".08em"}}>{l}</div>
                      <div style={{...MONO,fontSize:12,fontWeight:800,color:c,marginTop:2}}>{v2}</div>
                    </div>
                  ))}
                </div>
              </div>
            );})}
          </div>
        ) : (
          <div className="isp-tbl-wrap">
            <table className="isp-tbl">
              <thead><tr style={{background:"var(--paper-alt)"}}>
                <th className="tl" style={{width:"4%",paddingLeft:14}}>#</th>
                <th className={`tl${sk==="name"?" sorted":""}`} style={{width:"22%",cursor:"pointer"}} onClick={()=>hs("name")}>Stratégie <SortIcon active={sk==="name"} asc={asc} /></th>
                <th className="tl" style={{width:"16%"}}>Investisseur</th>
                <th className="tl" style={{width:"10%"}}>Philosophie</th>
                <th className={sk==="fy"?"sorted":""} style={{width:"7%",cursor:"pointer"}} onClick={()=>hs("fy")}><Tip text={`Performance annuelle complète\n(du 1er janv. au 31 déc.)\nHors dividendes réinvestis`}>FY</Tip> <SortIcon active={sk==="fy"} asc={asc} /></th>
                {view==="perf"?<>
                  <th style={{width:"9%"}}>Mensuel</th>
                  <th className={sk==="cagr3"?"sorted":""} style={{cursor:"pointer"}} onClick={()=>hs("cagr3")}><Tip text={`Taux de croissance annuel composé\nsur 3 ans. Ex: +12% = le capital\ndouble en ~6 ans à ce rythme`}>CAGR 3Y</Tip> <SortIcon active={sk==="cagr3"} asc={asc} /></th>
                  <th className={sk==="cagr5"?"sorted":""} style={{cursor:"pointer"}} onClick={()=>hs("cagr5")}><Tip text={`Taux de croissance annuel composé\nsur 5 ans. Mesure la performance\nlissée sur le long terme`}>CAGR 5Y</Tip> <SortIcon active={sk==="cagr5"} asc={asc} /></th>
                  <th className={sk==="sh1"?"sorted":""} style={{cursor:"pointer"}} onClick={()=>hs("sh1")}><Tip text={`Ratio de Sharpe : rendement/risque.\n>1 = bon · >2 = excellent.\nPlus c'est haut, mieux c'est`}>Sharpe 1Y</Tip> <SortIcon active={sk==="sh1"} asc={asc} /></th>
                  <th className={sk==="mdd1"?"sorted":""} style={{cursor:"pointer"}} onClick={()=>hs("mdd1")}><Tip text={`Maximum Drawdown : perte maximale\ndepuis un plus haut sur 1 an.\nEx: -20% = forte volatilité`}>MDD 1Y</Tip> <SortIcon active={sk==="mdd1"} asc={asc} /></th>
                </>:<>
                  <th className={sk==="sh1"?"sorted":""} style={{cursor:"pointer"}} onClick={()=>hs("sh1")}><Tip text={`Ratio de Sharpe 1 an.\nRendement ajusté du risque.\n>1 = acceptable · >2 = excellent`}>Sh. 1Y</Tip> <SortIcon active={sk==="sh1"} asc={asc} /></th>
                  <th>Sh. 3Y</th>
                  <th className={sk==="mdd1"?"sorted":""} style={{cursor:"pointer"}} onClick={()=>hs("mdd1")}><Tip text={`Maximum Drawdown : perte maximale\ndepuis un plus haut sur 1 an.\nEx: -20% = forte volatilité`}>MDD 1Y</Tip> <SortIcon active={sk==="mdd1"} asc={asc} /></th>
                  <th>MDD 3Y</th><th>Vol. 1Y</th>
                </>}
              </tr></thead>
              <tbody>
                {displayed.map((s,i)=>{const v=getFY(s);const iName=stratInv[s.name];return(
                  <tr key={s.id} className={drawerStratId===s.id?"sel":""} onClick={()=>onDrawer(s.id)}>
                    <td className="tl" style={{paddingLeft:14}}><span style={{...MONO,fontSize:13,color:"var(--muted)"}}>{medal(i)}</span></td>
                    <td className="tl"><div className="isp-strat-name">{s.name}</div><div className="isp-strat-sub" style={{...MONO}}>{s.selYear}→{s.analYear}</div></td>
                    <td className="tl" onClick={e=>e.stopPropagation()}>
                      {iName&&<span style={{display:"inline-flex",alignItems:"center",gap:5,cursor:"pointer"}} onClick={()=>onInvestor(iName)}>
                        <Avatar name={iName} size={18}/>
                        <span style={{fontSize:14,fontWeight:700,color:"var(--ink)"}}>{iName}</span>
                      </span>}
                    </td>
                    <td className="tl" onClick={e=>e.stopPropagation()}>
                      {iName && investors[iName] ? (
                        investors[iName].style ? (
                          <StylePill
                            s={investors[iName].style}
                            title={investors[iName].philosophy || undefined}
                          />
                        ) : (
                          <span style={{ color: "var(--muted)" }}>—</span>
                        )
                      ) : null}
                    </td>
                    <td><span className="isp-strat-val" style={{...MONO,fontWeight:700,color:v!=null?pC(v):"var(--muted)"}}>{fP(v)}</span></td>
                    {view==="perf"?<>
                      <td onClick={e=>{e.stopPropagation();setSparkStrat(s);}} style={{cursor:"pointer"}} title="Voir le graphique mensuel"><Sparkbar monthly={s.monthly}/></td>
                      <td><span className="isp-strat-val" style={{...MONO,fontWeight:700,fontSize:15,color:s.c3!=null?pC(s.c3):"var(--muted)"}}>{fP(s.c3)}</span></td>
                      <td><span className="isp-strat-val" style={{...MONO,fontWeight:700,fontSize:15,color:s.c5!=null?pC(s.c5):"var(--muted)"}}>{fP(s.c5)}</span></td>
                      <td><span className="isp-strat-val" style={{...MONO,fontWeight:700,fontSize:15,color:shC(s.sh1)}}>{s.sh1!=null?s.sh1.toFixed(2):"—"}</span></td>
                      <td><span className="isp-strat-val" style={{...MONO,fontWeight:700,fontSize:15,color:s.mdd1!=null?pC(s.mdd1):"var(--muted)"}}>{fP(s.mdd1)}</span></td>
                    </>:<>
                      <td><span className="isp-strat-val" style={{...MONO,fontWeight:700,fontSize:15,color:shC(s.sh1)}}>{s.sh1!=null?s.sh1.toFixed(2):"—"}</span></td>
                      <td><span className="isp-strat-val" style={{...MONO,fontWeight:700,fontSize:15,color:shC(s.sh3)}}>{s.sh3!=null?s.sh3.toFixed(2):"—"}</span></td>
                      <td><span className="isp-strat-val" style={{...MONO,fontWeight:700,fontSize:15,color:s.mdd1!=null?pC(s.mdd1):"var(--muted)"}}>{fP(s.mdd1)}</span></td>
                      <td><span className="isp-strat-val" style={{...MONO,fontWeight:700,fontSize:15,color:s.mdd3!=null?pC(s.mdd3):"var(--muted)"}}>{fP(s.mdd3)}</span></td>
                      <td><span style={{...MONO,fontWeight:700,fontSize:15,color:C.amber}}>{s.s1!=null?s.s1.toFixed(1)+"%":"—"}</span></td>
                    </>}
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── ROOT ───────────────────────────────────────────────────── */
export default function IndexStrategiesPage({
  pageData = null,
  loading = false,
  onRequestConstituents = null,
  onRequestInvestor = null,
  onOpenTicker = null,
}) {
  useCSS();
  const [modal,setModal]=useState(null);
  const [view,setView]=useState("perf");
  const [isMobile,setIsMobile]=useState(()=>typeof window!=="undefined"&&window.innerWidth<768);
  useEffect(()=>{const fn=()=>setIsMobile(window.innerWidth<768);window.addEventListener("resize",fn);return()=>window.removeEventListener("resize",fn);},[]);

  const hasApi = pageData?.allS?.length > 0;

  const handleOpenTicker = useCallback(
    (sym) => {
      if (modal) {
        saveDrillReturnState("strategies", {
          modalType: modal.type,
          stratId: modal.stratId ?? null,
          name: modal.name ?? null,
          fromStratId: modal.fromStratId ?? null,
        });
      }
      if (onOpenTicker) onOpenTicker(sym);
    },
    [modal, onOpenTicker]
  );

  const ctxValue = useMemo(() => {
    if (!hasApi) {
      return {
        stratInv: STRAT_INV,
        investors: INVESTORS,
        allS: null,
        allComp: null,
        loading,
        onRequestConstituents,
        onOpenTicker: handleOpenTicker,
      };
    }
    return {
      stratInv: pageData.stratInv || {},
      investors: pageData.investorsMap || {},
      allS: pageData.allS,
      allComp: pageData.allComp || [],
      loading,
      onRequestConstituents,
      onRequestInvestor,
      onOpenTicker: handleOpenTicker,
    };
  }, [pageData, hasApi, loading, onRequestConstituents, onRequestInvestor, handleOpenTicker]);

  const allS = hasApi ? pageData.allS : (loading ? [] : ALL_S);
  const allComp = hasApi ? (pageData.allComp || []) : (loading ? [] : RAW_COMP);

  function openStrategy(sid){
    const s = allS.find((x) => x.id === sid);
    if (s?.name && onRequestConstituents) onRequestConstituents(s.name);
    setModal({ type: "strategy", stratId: sid });
  }
  function openInvestor(name){
    if (name && onRequestInvestor) onRequestInvestor(name);
    const fromStratId=modal?.type==="strategy"?modal.stratId:null;
    setModal({type:"investor",name,fromStratId});
  }
  function closeModal(){setModal(null);}
  function backFromInvestor(){if(modal?.fromStratId)setModal({type:"strategy",stratId:modal.fromStratId});else closeModal();}
  function fromInvToStrat(sid){
    const s = allS.find((x) => x.id === sid);
    if (s?.name && onRequestConstituents) onRequestConstituents(s.name);
    setModal({type:"strategy",stratId:sid});
  }

  const modalRestoredRef = useRef(false);
  useEffect(() => {
    if (modalRestoredRef.current || loading) return;
    const saved = loadDrillReturnState("strategies");
    if (!saved?.modalType) return;
    modalRestoredRef.current = true;
    clearDrillReturnState("strategies");
    if (saved.modalType === "strategy" && saved.stratId != null) {
      const s = allS.find((x) => x.id === saved.stratId);
      if (s?.name && onRequestConstituents) onRequestConstituents(s.name);
      setModal({ type: "strategy", stratId: saved.stratId });
    } else if (saved.modalType === "investor" && saved.name) {
      if (onRequestInvestor) onRequestInvestor(saved.name);
      setModal({
        type: "investor",
        name: saved.name,
        fromStratId: saved.fromStratId ?? null,
      });
    }
  }, [loading, allS, onRequestConstituents, onRequestInvestor]);

  return (
    <StrategiesDataContext.Provider value={ctxValue}>
    <div className="isp-page-full">
      {loading && !pageData?.allS?.length && (
        <div style={{ padding: 24, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
          Chargement des stratégies…
        </div>
      )}
      {/* Main list — full width */}
      <StrategiesPage allS={allS} allComp={allComp} onDrawer={openStrategy} onInvestor={openInvestor} drawerStratId={modal?.type==="strategy"?modal.stratId:null} isMobile={isMobile} view={view} setView={setView}/>
      {/* Centered modals */}
      {modal?.type==="strategy"&&<StrategyPage stratId={modal.stratId} allS={allS} allComp={allComp} onClose={closeModal} onInvestor={openInvestor}/>}
      {modal?.type==="investor"&&<InvestorPage name={modal.name} allS={allS} onClose={backFromInvestor} onStrategy={fromInvToStrat}/>}
    </div>
    </StrategiesDataContext.Provider>
  );
}
